import React from 'react';
import { useApp, MedicalRecord, User, Appointment, PrescriptionItem, parseRecordTimestamp, getTodayDateString, getSafeUrl } from './AppContext';
import { Button } from './Components';
import { DigitalPrescriptionModal } from './DigitalPrescriptionModal';
import { DigitalPrescriptionGeneratorModal } from './DigitalPrescriptionGeneratorModal';
import { safeStringify } from './src/utils/safeJson';
import { registerActiveCallTab } from './useActiveCallStatus';
import { getApp } from "firebase/app";
import { getFirestore, doc, getDoc, collection, setDoc, updateDoc, onSnapshot, addDoc, query, orderBy } from "firebase/firestore";

export const ConsultationRoom: React.FC<{ appointmentId: string, onEnd: () => void }> = ({ appointmentId, onEnd }) => {
    const { user, allUsers, doctors, appointments, allAppointments, addMedicalRecord, uploadClinicalFile, adminUpdateAppointment, medicalRecords, sendSystemEmail, showToast } = useApp();
    const videoRef = React.useRef<HTMLVideoElement>(null);
    const remoteVideoRef = React.useRef<HTMLVideoElement>(null);
    const remoteAudioRef = React.useRef<HTMLAudioElement>(null);
    const localStreamRef = React.useRef<MediaStream | null>(null);
    const pcRef = React.useRef<RTCPeerConnection | null>(null);
    const unsubsRef = React.useRef<(() => void)[]>([]);
    const currentSessionIdRef = React.useRef<string>('');
    const remoteCandidatesQueue = React.useRef<RTCIceCandidateInit[]>([]);
    const hasSentWaitingEmailRef = React.useRef<boolean>(false);
    const milestoneToastRef = React.useRef<{ [key: number]: boolean }>({});

    const [stream, setStream] = React.useState<MediaStream | null>(null);
    const [remoteStream, setRemoteStream] = React.useState<MediaStream | null>(null);
    const [isMuted, setIsMuted] = React.useState(false);
    const [isVideoOff, setIsVideoOff] = React.useState(false);
    const [showRecordForm, setShowRecordForm] = React.useState(false);
    const [showPatientPanel, setShowPatientPanel] = React.useState(false);
    const [doctorWaitingSeconds, setDoctorWaitingSeconds] = React.useState<number>(0);
    const [showPatientNoResponseModal, setShowPatientNoResponseModal] = React.useState<boolean>(false);
    const [isRemoteParticipantInRoom, setIsRemoteParticipantInRoom] = React.useState<boolean>(false);
    const [activeTab, setActiveTab] = React.useState<'profile' | 'upload' | 'notes' | 'chat' | 'history'>('profile');
    const [chatMessages, setChatMessages] = React.useState<{id: string, senderId: string, senderName?: string, text: string, timestamp: string}[]>([]);
    const [newMessage, setNewMessage] = React.useState('');
    const [privateNotes, setPrivateNotes] = React.useState('');

    // --- Movable & Dockable Self-Camera Preview (PiP) State ---
    const [pipCorner, setPipCorner] = React.useState<'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'>('bottom-right');
    const [pipOffset, setPipOffset] = React.useState<{ x: number; y: number } | null>(null);
    const [isDraggingPip, setIsDraggingPip] = React.useState(false);
    const dragOriginRef = React.useRef<{ mouseX: number; mouseY: number; startX: number; startY: number } | null>(null);
    const pipContainerRef = React.useRef<HTMLDivElement>(null);
    const mainViewRef = React.useRef<HTMLDivElement>(null);

    // --- Video Display Fit & Zoom Controls ---
    const [remoteVideoFit, setRemoteVideoFit] = React.useState<'contain' | 'cover'>('contain');
    const [remoteScale, setRemoteScale] = React.useState<number>(1);
    const [localVideoFit, setLocalVideoFit] = React.useState<'contain' | 'cover'>('contain');
    const [cameraFacingMode, setCameraFacingMode] = React.useState<'user' | 'environment'>('user');
    const [hardwareZoomCapabilities, setHardwareZoomCapabilities] = React.useState<{ min: number; max: number; step: number } | null>(null);
    const [currentHardwareZoom, setCurrentHardwareZoom] = React.useState<number>(1);
    const [isPrescriptionBannerDismissed, setIsPrescriptionBannerDismissed] = React.useState<boolean>(false);

    // Rock-solid dragging with window event listeners to prevent vanishing on desktop
    const handlePipPointerDown = (e: React.PointerEvent) => {
        if (e.button !== 0 && e.pointerType === 'mouse') return;
        const mainStage = mainViewRef.current?.getBoundingClientRect();
        const pip = pipContainerRef.current?.getBoundingClientRect();
        if (!mainStage || !pip) return;

        const currentX = pipOffset ? pipOffset.x : (pip.left - mainStage.left);
        const currentY = pipOffset ? pipOffset.y : (pip.top - mainStage.top);

        dragOriginRef.current = {
            mouseX: e.clientX,
            mouseY: e.clientY,
            startX: currentX,
            startY: currentY
        };
        setIsDraggingPip(true);

        const handlePointerMove = (moveEvt: PointerEvent) => {
            if (!dragOriginRef.current || !mainViewRef.current || !pipContainerRef.current) return;
            const stageRect = mainViewRef.current.getBoundingClientRect();
            const pipRect = pipContainerRef.current.getBoundingClientRect();

            const deltaX = moveEvt.clientX - dragOriginRef.current.mouseX;
            const deltaY = moveEvt.clientY - dragOriginRef.current.mouseY;

            const rawX = dragOriginRef.current.startX + deltaX;
            const rawY = dragOriginRef.current.startY + deltaY;

            const maxX = Math.max(12, stageRect.width - pipRect.width - 12);
            const maxY = Math.max(12, stageRect.height - pipRect.height - 12);

            const clampedX = Math.max(12, Math.min(rawX, maxX));
            const clampedY = Math.max(12, Math.min(rawY, maxY));

            setPipOffset({ x: clampedX, y: clampedY });
        };

        const handlePointerUp = () => {
            setIsDraggingPip(false);
            dragOriginRef.current = null;
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            window.removeEventListener('pointercancel', handlePointerUp);
        };

        window.addEventListener('pointermove', handlePointerMove, { passive: true });
        window.addEventListener('pointerup', handlePointerUp, { once: true });
        window.addEventListener('pointercancel', handlePointerUp, { once: true });
    };

    // Re-clamp PiP if screen or sidebar resizes
    React.useEffect(() => {
        if (!pipOffset || !mainViewRef.current || !pipContainerRef.current) return;
        const stageRect = mainViewRef.current.getBoundingClientRect();
        const pipRect = pipContainerRef.current.getBoundingClientRect();
        const maxX = Math.max(12, stageRect.width - pipRect.width - 12);
        const maxY = Math.max(12, stageRect.height - pipRect.height - 12);
        if (pipOffset.x > maxX || pipOffset.y > maxY) {
            setPipOffset({
                x: Math.max(12, Math.min(pipOffset.x, maxX)),
                y: Math.max(12, Math.min(pipOffset.y, maxY))
            });
        }
    }, [showPatientPanel]);

    const cyclePipCorner = (e: React.MouseEvent) => {
        e.stopPropagation();
        setPipOffset(null);
        const corners: ('bottom-right' | 'bottom-left' | 'top-left' | 'top-right')[] = ['bottom-right', 'bottom-left', 'top-left', 'top-right'];
        const nextIndex = (corners.indexOf(pipCorner) + 1) % corners.length;
        setPipCorner(corners[nextIndex]);
    };
    
    // Digital Prescription In-Call States
    const [showPrescriptionGenerator, setShowPrescriptionGenerator] = React.useState<boolean>(false);
    const [viewingPrescriptionRecord, setViewingPrescriptionRecord] = React.useState<MedicalRecord | null>(null);
    const [issuedPrescriptionRxId, setIssuedPrescriptionRxId] = React.useState<string | null>(null);
    const [showPrescriptionHistoryModal, setShowPrescriptionHistoryModal] = React.useState<boolean>(false);
    
    // In-Call Patient Medical Record Upload State
    const [patientUploadFiles, setPatientUploadFiles] = React.useState<File[]>([]);
    const [patientUploadTitle, setPatientUploadTitle] = React.useState<string>('');
    const [patientUploadNotes, setPatientUploadNotes] = React.useState<string>('');
    const [isPatientUploading, setIsPatientUploading] = React.useState<boolean>(false);
    const [patientUploadFeedback, setPatientUploadFeedback] = React.useState<{ type: 'success' | 'error', text: string } | null>(null);

    // Comprehensive Post-Consultation Clinical Fields & Auto-save
    const [recordData, setRecordData] = React.useState({ 
        presentingSymptoms: '',
        history: '',
        pastHistory: '',
        socialHistory: '',
        vitals: '',
        physicalExam: '',
        labReports: '',
        imaging: '',
        diagnosis: '', 
        treatmentPlan: '', 
        prescription: '', 
        followUp: '',
        referrals: '',
        notes: ''
    });
    const [hasRestoredConsultationDraft, setHasRestoredConsultationDraft] = React.useState(false);
    const [isDraftSaved, setIsDraftSaved] = React.useState(false);

    // Auto-restore consultation draft if doctor previously exited mistakenly
    React.useEffect(() => {
        if (!appointmentId || typeof window === 'undefined') return;
        try {
            const draftKey = `rovita_consultation_draft_${appointmentId}`;
            const savedDraft = localStorage.getItem(draftKey);
            if (savedDraft) {
                const parsed = JSON.parse(savedDraft);
                if (parsed && typeof parsed === 'object') {
                    setRecordData(prev => ({ ...prev, ...parsed }));
                    setHasRestoredConsultationDraft(true);
                }
            }
        } catch (e) {
            console.debug("Draft restore debug:", e);
        }
    }, [appointmentId]);

    // Live auto-save post consultation form fields to localStorage
    React.useEffect(() => {
        if (!appointmentId || typeof window === 'undefined') return;
        const hasContent = Object.values(recordData).some(val => typeof val === 'string' && val.trim().length > 0);
        if (!hasContent) return;

        const draftKey = `rovita_consultation_draft_${appointmentId}`;
        const timeout = setTimeout(() => {
            try {
                localStorage.setItem(draftKey, safeStringify(recordData));
                setIsDraftSaved(true);
            } catch (e) {}
        }, 500);

        return () => clearTimeout(timeout);
    }, [recordData, appointmentId]);

    const [selectedAttachments, setSelectedAttachments] = React.useState<File[]>([]);
    const [isUploading, setIsUploading] = React.useState(false);
    const [peerStatus, setPeerStatus] = React.useState<'hardware_init' | 'signaling' | 'handshake' | 'connected' | 'failed' | 'disconnected' | 'calling' | 'waiting'>('hardware_init');
    const [diagnostic, setDiagnostic] = React.useState<string>('Initializing hardware...');
    const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
    const [startTime] = React.useState(new Date().toISOString());

    // --- WebRTC Internal Telemetry State ---
    const [diagnosticLogs, setDiagnosticLogs] = React.useState<Array<{ id: string; time: string; msg: string; type: 'info' | 'success' | 'warn' | 'error' }>>([]);
    const [webrtcStats, setWebrtcStats] = React.useState({
        signalingState: 'uninitialized',
        iceConnectionState: 'uninitialized',
        connectionState: 'uninitialized',
        localCandidatesCount: 0,
        remoteCandidatesCount: 0,
        localAudioTrack: false,
        localVideoTrack: false,
        remoteTracksCount: 0
    });

    const addLog = React.useCallback((msg: string, type: 'info' | 'success' | 'warn' | 'error' = 'info') => {
        const time = new Date().toLocaleTimeString();
        setDiagnosticLogs(prev => [
            { id: `${Date.now()}_${Math.random().toString(36).substring(2, 6)}`, time, msg, type },
            ...prev.slice(0, 49) // Keep last 50 entries
        ]);
        console.log(`[WebRTC Diagnostic ${time}] ${msg}`);
    }, []);

    // --- Direct Appointment Resolution ---
    const [appointment, setAppointment] = React.useState<Appointment | null>(() => {
        return appointments.find(a => a.id === appointmentId) || (allAppointments && allAppointments.find(a => a.id === appointmentId)) || null;
    });

    React.useEffect(() => {
        const found = appointments.find(a => a.id === appointmentId) || (allAppointments && allAppointments.find(a => a.id === appointmentId));
        if (found) {
            setAppointment(found);
        } else {
            const db = getFirestore(getApp());
            getDoc(doc(db, 'appointments', appointmentId)).then(snap => {
                if (snap.exists()) {
                    setAppointment({ id: snap.id, ...snap.data() } as Appointment);
                }
            }).catch(e => console.debug("Appointment direct load notice:", e));
        }
    }, [appointmentId, appointments, allAppointments]);

    // Role calculations
    const isDoctor = user?.role === 'doctor' || (appointment && appointment.doctorId === user?.id);
    const patientUser = allUsers.find(u => u.id === appointment?.patientId) || (user?.role === 'patient' ? user : undefined);
    const doctorUser = allUsers.find(u => u.id === appointment?.doctorId) || doctors.find(d => d.id === appointment?.doctorId) || (isDoctor ? user : undefined);

    const displayDoctorName = appointment?.doctorName 
        ? (appointment.doctorName.toLowerCase().startsWith('dr') ? appointment.doctorName : `Dr. ${appointment.doctorName}`)
        : (doctorUser?.name ? (doctorUser.name.toLowerCase().startsWith('dr') ? doctorUser.name : `Dr. ${doctorUser.name}`) : 'Dr. Specialist');

    const displayPatientName = appointment?.patientName || patientUser?.name || 'Patient';

    // Cross-tab active call registration: prevents duplicate joins from other tabs
    React.useEffect(() => {
        const cleanup = registerActiveCallTab(appointmentId, Boolean(isDoctor), peerStatus === 'connected');
        return cleanup;
    }, [appointmentId, isDoctor, peerStatus]);

    // --- Synchronized Persistent Countdown Timer State ---
    const [sessionSecondsRemaining, setSessionSecondsRemaining] = React.useState<number>(1800); // 30 minutes default
    const [totalSessionDuration, setTotalSessionDuration] = React.useState<number>(1800);
    const callStartedAtRef = React.useRef<number | null>(null);
    const totalDurationRef = React.useRef<number>(1800);

    // --- Mobile Responsive Orientation State ---
    const isMobileDevice = typeof window !== 'undefined' && (window.innerWidth < 768 || /Android|iPhone|iPad/i.test(navigator.userAgent));
    const [isPortraitMode, setIsPortraitMode] = React.useState<boolean>(isMobileDevice);

    // Identity & Patient Details Verification Checklist
    const [verification, setVerification] = React.useState({ 
        idConfirmed: false, 
        addressConfirmed: false, 
        phoneConfirmed: false, 
        emailConfirmed: false, 
        consentConfirmed: false,
        locationConfirmed: false 
    });

    // Continuous real-time countdown relative to persistent session start
    React.useEffect(() => {
        const timer = setInterval(() => {
            if (callStartedAtRef.current && totalDurationRef.current) {
                const elapsed = Math.max(0, Math.floor((Date.now() - callStartedAtRef.current) / 1000));
                const remaining = Math.max(0, totalDurationRef.current - elapsed);
                setSessionSecondsRemaining(remaining);
            } else {
                setSessionSecondsRemaining(prev => (prev > 0 ? prev - 1 : 0));
            }
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // --- DOCTOR WAITING TIMER (30s Email Alert & 2-Minute Patient Arrival Notice) ---
    React.useEffect(() => {
        // If not doctor, or remote video connected, or peerStatus connected, or patient is in room, or patientOnline: reset & do not count
        if (!isDoctor || remoteStream || peerStatus === 'connected' || isRemoteParticipantInRoom || appointment?.patientOnline) {
            setDoctorWaitingSeconds(0);
            setShowPatientNoResponseModal(false);
            return;
        }

        const timer = setInterval(() => {
            setDoctorWaitingSeconds(prev => {
                const next = prev + 1;

                // 30-Second Milestone: Patient Reminder Email & In-App Notification
                if (next >= 30 && !hasSentWaitingEmailRef.current) {
                    hasSentWaitingEmailRef.current = true;
                    
                    const patientEmail = patientUser?.email || (appointment as any)?.patientEmail;
                    if (patientEmail && sendSystemEmail) {
                        const subject = `Dr. ${displayDoctorName} is ready in your ROVITA consultation room`;
                        const emailHtml = `
                            <!DOCTYPE html>
                            <html lang="en">
                            <head>
                              <meta charset="UTF-8">
                              <meta name="viewport" content="width=device-width, initial-scale=1.0">
                              <title>Doctor is Waiting in Consultation Room</title>
                            </head>
                            <body style="margin: 0; padding: 0; background-color: #f8fafc; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #1e293b;">
                              <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f8fafc; padding: 24px 0;">
                                <tr>
                                  <td align="center">
                                    <table border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width: 580px; background-color: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);">
                                      <tr>
                                        <td style="padding: 28px 32px 20px 32px; background: linear-gradient(135deg, #008080, #0f766e); text-align: center;">
                                          <h1 style="margin: 0; color: #ffffff; font-size: 22px; font-weight: 800; letter-spacing: -0.5px;">ROVITA Virtual Clinic</h1>
                                          <p style="margin: 6px 0 0 0; color: #ccfbf1; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 1.5px;">Consultation Room Active</p>
                                        </td>
                                      </tr>
                                      <tr>
                                        <td style="padding: 28px 32px;">
                                          <div style="background-color: #f0fdfa; border-left: 4px solid #008080; padding: 16px; border-radius: 6px; margin-bottom: 20px;">
                                            <p style="color: #0f766e; font-weight: 700; margin: 0 0 4px 0; font-size: 15px;">Your Physician is in the Room</p>
                                            <p style="color: #334155; font-size: 14px; margin: 0; line-height: 1.5;">
                                              Dr. <strong>${displayDoctorName}</strong> has opened your scheduled video consultation on ROVITA and is ready to connect with you.
                                            </p>
                                          </div>
                                          <p style="font-size: 14px; line-height: 1.6; color: #475569; margin: 0 0 24px 0;">
                                            Please open your ROVITA Health Portal and click <strong>Join Consultation</strong> on your dashboard.
                                          </p>
                                          <table border="0" cellpadding="0" cellspacing="0" width="100%" style="margin-bottom: 24px;">
                                            <tr>
                                              <td align="center">
                                                <a href="${typeof window !== 'undefined' ? window.location.origin : 'https://rovita.co'}/#dashboard" style="display: inline-block; background-color: #008080; color: #ffffff; padding: 12px 28px; border-radius: 10px; font-weight: bold; font-size: 14px; text-decoration: none;">
                                                  Enter Consultation Room
                                                </a>
                                              </td>
                                            </tr>
                                          </table>
                                          <p style="font-size: 13px; color: #64748b; margin: 0;">
                                            Warm regards,<br />
                                            <strong style="color: #1e293b;">The ROVITA Clinical Care Team</strong>
                                          </p>
                                        </td>
                                      </tr>
                                      <tr>
                                        <td style="padding: 16px 32px; background-color: #f8fafc; border-top: 1px solid #e2e8f0; text-align: center; font-size: 12px; color: #94a3b8;">
                                          Appointment ID: ${appointmentId} • ROVITA Virtual Clinic
                                        </td>
                                      </tr>
                                    </table>
                                  </td>
                                </tr>
                              </table>
                            </body>
                            </html>
                        `;
                        sendSystemEmail(patientEmail, subject, emailHtml).catch(err => {
                            console.warn("Could not dispatch 30s patient reminder email:", err);
                        });

                        // Record system message in in-call transcript
                        const db = getFirestore(getApp());
                        addDoc(collection(db, 'calls', appointmentId, 'messages'), {
                            senderId: 'system',
                            senderName: 'ROVITA System',
                            text: `🔔 Notice: A reminder email was sent to patient (${patientEmail}) letting them know Dr. ${displayDoctorName} is waiting in the room.`,
                            timestamp: new Date().toISOString()
                        }).catch(() => {});
                    }

                    if (!milestoneToastRef.current[30]) {
                        milestoneToastRef.current[30] = true;
                        showToast('Reminder Sent', "We've emailed a reminder to your patient letting them know you are waiting in the room.", 'info');
                    }
                }

                // 60-Second Milestone: 1 Minute Elapsed Notice
                if (next >= 60 && !milestoneToastRef.current[60]) {
                    milestoneToastRef.current[60] = true;
                    showToast('Waiting for Patient', 'Still waiting for your patient to connect (1 minute remaining).', 'info');
                }

                // 90-Second Milestone: 30 Seconds Remaining Notice
                if (next >= 90 && !milestoneToastRef.current[90]) {
                    milestoneToastRef.current[90] = true;
                    showToast('Call Notice', "Your patient hasn't connected yet. You can keep waiting or return to dashboard.", 'info');
                }

                // 2-Minute (120s) Arrival Prompt
                if (next >= 120) {
                    clearInterval(timer);
                    setShowPatientNoResponseModal(true);
                }

                return next;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [isDoctor, remoteStream, peerStatus, isRemoteParticipantInRoom, patientUser, appointment, displayDoctorName, appointmentId, sendSystemEmail, showToast]);

    const formatTimer = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleExtendSession = async (mins: number) => {
        const newDuration = totalDurationRef.current + mins * 60;
        totalDurationRef.current = newDuration;
        setTotalSessionDuration(newDuration);
        
        if (callStartedAtRef.current) {
            const elapsed = Math.max(0, Math.floor((Date.now() - callStartedAtRef.current) / 1000));
            setSessionSecondsRemaining(Math.max(0, newDuration - elapsed));
        } else {
            setSessionSecondsRemaining(prev => prev + mins * 60);
        }

        try {
            const db = getFirestore(getApp());
            await updateDoc(doc(db, 'calls', appointmentId), { totalDurationSeconds: newDuration }).catch(() => {});
        } catch (e) {
            console.debug("Session extension sync debug:", e);
        }
    };

    // Patient In-Call Medical Record Upload Handler
    const handlePatientUploadRecord = async (e: React.FormEvent) => {
        e.preventDefault();
        if (patientUploadFiles.length === 0 || !user) return;

        setIsPatientUploading(true);
        setPatientUploadFeedback(null);

        try {
            const uploadResults = await Promise.all(patientUploadFiles.map(f => uploadClinicalFile(f)));
            
            const docTitle = patientUploadTitle.trim() || (patientUploadFiles.length === 1 ? patientUploadFiles[0].name : `Shared Documents (${patientUploadFiles.length} files)`);
            const newPatientRecord: Omit<MedicalRecord, 'id'> = {
                patientId: user.id,
                doctorId: appointment?.doctorId || '',
                doctorName: displayDoctorName,
                date: getTodayDateString(),
                authoredBy: 'patient',
                diagnosis: docTitle,
                notes: patientUploadNotes.trim() || 'Uploaded by patient during live video consultation',
                prescription: '',
                attachmentUrl: uploadResults[0]?.url || '',
                attachmentName: uploadResults[0]?.name || '',
                attachments: uploadResults.map((r, i) => ({
                    name: r.name,
                    url: r.url,
                    size: patientUploadFiles[i]?.size,
                    type: patientUploadFiles[i]?.type
                }))
            };

            await addMedicalRecord(newPatientRecord);

            // Post notice into in-call chat so doctor is alerted immediately
            const db = getFirestore(getApp());
            await addDoc(collection(db, 'calls', appointmentId, 'messages'), {
                senderId: user.id,
                senderName: user.name,
                text: `📎 Patient uploaded ${patientUploadFiles.length} medical file(s): ${docTitle}`,
                timestamp: new Date().toISOString()
            });

            setPatientUploadFeedback({
                type: 'success',
                text: `${patientUploadFiles.length} file(s) uploaded and shared with provider in real time!`
            });
            setPatientUploadFiles([]);
            setPatientUploadTitle('');
            setPatientUploadNotes('');
        } catch (err: any) {
            console.error("Patient in-call upload error:", err);
            setPatientUploadFeedback({
                type: 'error',
                text: err?.message || 'Failed to upload document(s). Please try again.'
            });
        } finally {
            setIsPatientUploading(false);
        }
    };

    // --- CAMERA HARDWARE ZOOM & FACING CONTROLS ---
    const handleSetCameraZoom = async (newZoom: number) => {
        const currentStream = localStreamRef.current;
        if (!currentStream) return;
        const vTrack = currentStream.getVideoTracks()[0];
        if (!vTrack) return;
        try {
            if (typeof (vTrack as any).applyConstraints === 'function') {
                await (vTrack as any).applyConstraints({
                    advanced: [{ zoom: newZoom }]
                });
                setCurrentHardwareZoom(newZoom);
            }
        } catch (err) {
            console.debug("Camera hardware zoom apply error:", err);
        }
    };

    const handleSwitchCameraFacing = async () => {
        const nextFacing = cameraFacingMode === 'user' ? 'environment' : 'user';
        setCameraFacingMode(nextFacing);
        const currentStream = localStreamRef.current;
        if (!currentStream) return;
        const videoTrack = currentStream.getVideoTracks()[0];

        try {
            if (videoTrack && typeof videoTrack.applyConstraints === 'function') {
                await videoTrack.applyConstraints({
                    facingMode: nextFacing,
                    width: { ideal: isPortraitMode ? 720 : 1280, max: 1920 },
                    height: { ideal: isPortraitMode ? 1280 : 720, max: 1080 }
                });
                return;
            }
        } catch (_) {}

        try {
            const newStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: nextFacing,
                    width: { ideal: isPortraitMode ? 720 : 1280, max: 1920 },
                    height: { ideal: isPortraitMode ? 1280 : 720, max: 1080 }
                },
                audio: false
            });
            const newTrack = newStream.getVideoTracks()[0];
            if (newTrack && videoTrack) {
                newTrack.enabled = !isVideoOff;
                videoTrack.stop();
                currentStream.removeTrack(videoTrack);
                currentStream.addTrack(newTrack);

                if (videoRef.current) {
                    videoRef.current.srcObject = currentStream;
                    videoRef.current.play().catch(() => {});
                }

                if (pcRef.current) {
                    const videoSender = pcRef.current.getSenders().find(s => s.track?.kind === 'video');
                    if (videoSender) {
                        videoSender.replaceTrack(newTrack);
                    }
                }

                if (typeof (newTrack as any).getCapabilities === 'function') {
                    const caps = (newTrack as any).getCapabilities();
                    if (caps && 'zoom' in caps) {
                        setHardwareZoomCapabilities({
                            min: caps.zoom.min || 1,
                            max: caps.zoom.max || 3,
                            step: caps.zoom.step || 0.1
                        });
                    }
                }
            }
        } catch (err) {
            console.error("Camera facing switch error:", err);
        }
    };

    const updateStreamForOrientation = async (targetIsPortrait: boolean) => {
        setIsPortraitMode(targetIsPortrait);

        const currentStream = localStreamRef.current;
        if (!currentStream) return;

        const videoTrack = currentStream.getVideoTracks()[0];
        if (!videoTrack) return;

        // Wide full-angle camera constraints without artificial zoom/aspect cropping
        const newVideoConstraints: MediaTrackConstraints = {
            facingMode: cameraFacingMode,
            width: { ideal: targetIsPortrait ? 720 : 1280, max: 1920 },
            height: { ideal: targetIsPortrait ? 1280 : 720, max: 1080 }
        };

        try {
            if (typeof videoTrack.applyConstraints === 'function') {
                await videoTrack.applyConstraints(newVideoConstraints);
                return;
            }
        } catch (e) {
            console.debug("Track applyConstraints fallback:", e);
        }

        try {
            const newMediaStream = await navigator.mediaDevices.getUserMedia({
                video: newVideoConstraints,
                audio: false
            });
            const newVideoTrack = newMediaStream.getVideoTracks()[0];
            if (newVideoTrack) {
                newVideoTrack.enabled = !isVideoOff;
                
                videoTrack.stop();
                currentStream.removeTrack(videoTrack);
                currentStream.addTrack(newVideoTrack);

                if (videoRef.current) {
                    videoRef.current.srcObject = currentStream;
                    videoRef.current.play().catch(() => {});
                }

                if (pcRef.current) {
                    const videoSender = pcRef.current.getSenders().find(s => s.track?.kind === 'video');
                    if (videoSender) {
                        videoSender.replaceTrack(newVideoTrack);
                    }
                }
            }
        } catch (err) {
            console.error("Orientation track replace error:", err);
        }
    };

    const toggleCameraOrientation = async () => {
        await updateStreamForOrientation(!isPortraitMode);
    };

    // --- AUTOMATIC MOBILE ORIENTATION HANDLER ---
    React.useEffect(() => {
        let lastIsPortrait: boolean | null = null;

        const checkAndApplyOrientation = () => {
            let isPortrait = false;
            if (window.screen && window.screen.orientation) {
                isPortrait = window.screen.orientation.type.startsWith('portrait');
            } else if (typeof window.orientation !== 'undefined') {
                isPortrait = Math.abs(window.orientation as number) !== 90;
            } else {
                isPortrait = window.innerHeight > window.innerWidth;
            }

            if (lastIsPortrait !== isPortrait) {
                lastIsPortrait = isPortrait;
                updateStreamForOrientation(isPortrait);
            }
        };

        const handleScreenOrientationChange = () => {
            checkAndApplyOrientation();
        };

        const mediaQuery = window.matchMedia('(orientation: portrait)');
        const handleMediaQueryChange = (e: MediaQueryListEvent) => {
            if (lastIsPortrait !== e.matches) {
                lastIsPortrait = e.matches;
                updateStreamForOrientation(e.matches);
            }
        };

        if (window.screen && window.screen.orientation) {
            window.screen.orientation.addEventListener('change', handleScreenOrientationChange);
        }
        window.addEventListener('orientationchange', handleScreenOrientationChange);
        window.addEventListener('resize', handleScreenOrientationChange);

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleMediaQueryChange);
        } else if ((mediaQuery as any).addListener) {
            (mediaQuery as any).addListener(handleMediaQueryChange);
        }

        return () => {
            if (window.screen && window.screen.orientation) {
                window.screen.orientation.removeEventListener('change', handleScreenOrientationChange);
            }
            window.removeEventListener('orientationchange', handleScreenOrientationChange);
            window.removeEventListener('resize', handleScreenOrientationChange);

            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleMediaQueryChange);
            } else if ((mediaQuery as any).removeListener) {
                (mediaQuery as any).removeListener(handleMediaQueryChange);
            }
        };
    }, []);

    // --- CLEANUP FUNCTION ---
    const cleanupMedia = () => {
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(track => {
                track.stop();
                track.enabled = false;
            });
            localStreamRef.current = null;
        }
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
        }
        if (pcRef.current) {
            pcRef.current.close();
            pcRef.current = null;
        }
        unsubsRef.current.forEach(unsub => unsub());
        unsubsRef.current = [];
        remoteCandidatesQueue.current = [];
        setStream(null);
        setRemoteStream(null);
    };

    const [callEndedByRemote, setCallEndedByRemote] = React.useState<string | null>(null);

    const handleEndSession = async (isCompleted: boolean = false) => {
        try {
            const db = getFirestore(getApp());
            const callDocRef = doc(db, 'calls', appointmentId);
            
            // Mark user presence offline and call ended in call signaling document
            await updateDoc(callDocRef, {
                status: isCompleted ? 'completed' : 'ended',
                callEnded: true,
                inProgress: false,
                doctorOnline: false,
                patientOnline: false,
                bothConnected: false,
                endedAt: Date.now(),
                endedBy: user?.id || 'unknown',
                endedByName: user?.name || (isDoctor ? 'Doctor' : 'Patient')
            }).catch(() => {});

            // Record call ended in local storage and notify cross-tab channels immediately
            if (typeof window !== 'undefined') {
                try {
                    localStorage.setItem(`rovita_ended_call_${appointmentId}`, Date.now().toString());
                    const raw = localStorage.getItem('rovita_active_call_session');
                    if (raw) {
                        try {
                            const parsed = JSON.parse(raw);
                            if (parsed.appointmentId === appointmentId) {
                                localStorage.removeItem('rovita_active_call_session');
                            }
                        } catch (e) {}
                    }
                    if ('BroadcastChannel' in window) {
                        const bc = new BroadcastChannel('rovita_call_sync_channel');
                        bc.postMessage({ type: 'CALL_ENDED', appointmentId });
                        bc.close();
                    }
                } catch (e) {}
            }

            // If the appointment was not finished/completed, reset back to upcoming so that neither party gets stuck
            if (!isCompleted && appointment && appointment.status !== 'completed' && appointment.status !== 'cancelled') {
                await adminUpdateAppointment(appointmentId, { status: 'upcoming' }).catch(() => {});
            }
        } catch (err) {
            console.warn("Session end presence cleanup warning:", err);
        } finally {
            cleanupMedia();
            onEnd();
        }
    };

    const [isAudioAutoplayBlocked, setIsAudioAutoplayBlocked] = React.useState(false);

    const handleRemoteStream = (rs: MediaStream) => {
        setRemoteStream(rs);
        setPeerStatus('connected');
        setDiagnostic('Consultation Connected');
        if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = rs;
            remoteVideoRef.current.muted = false;
            remoteVideoRef.current.play().then(() => {
                setIsAudioAutoplayBlocked(false);
            }).catch(e => {
                console.debug("Remote video play debug:", e);
                if (e && (e.name === 'NotAllowedError' || String(e).includes('user gesture') || String(e).includes('autoplay'))) {
                    setIsAudioAutoplayBlocked(true);
                }
            });
        }
        if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = rs;
            remoteAudioRef.current.muted = false;
            remoteAudioRef.current.play().catch(() => {});
        }
    };

    const drainQueuedCandidates = async (pc: RTCPeerConnection) => {
        while (remoteCandidatesQueue.current.length > 0) {
            const candidateData = remoteCandidatesQueue.current.shift();
            if (candidateData) {
                try {
                    await pc.addIceCandidate(new RTCIceCandidate(candidateData));
                } catch (e) {
                    console.debug("Error adding queued ICE candidate:", e);
                }
            }
        }
    };

    const reconnectCall = async () => {
        addLog('User requested consultation link refresh. Resetting peer connection...', 'info');
        setDiagnostic('Re-establishing consultation video link...');
        setPeerStatus('signaling');
        if (pcRef.current) {
            try {
                pcRef.current.close();
            } catch (_) {}
            pcRef.current = null;
        }
        unsubsRef.current.forEach(u => u());
        unsubsRef.current = [];
        remoteCandidatesQueue.current = [];
        if (localStreamRef.current) {
            await initWebRTC(localStreamRef.current);
        } else {
            await startMedia();
        }
    };

    const initWebRTC = async (s: MediaStream) => {
        if (!user || pcRef.current) return;
        const db = getFirestore(getApp());
        
        addLog(`Initiating WebRTC for appointment ${appointmentId.slice(0, 8)}... (${isDoctor ? 'Role: Doctor / Offerer' : 'Role: Patient / Answerer'})`, 'info');
        setWebrtcStats(prev => ({
            ...prev,
            localAudioTrack: s.getAudioTracks().length > 0,
            localVideoTrack: s.getVideoTracks().length > 0
        }));

        const servers: RTCConfiguration = {
            iceServers: [
                { urls: 'stun:stun.l.google.com:19302' },
                { urls: 'stun:stun1.l.google.com:19302' },
                { urls: 'stun:stun2.l.google.com:19302' },
                { urls: 'stun:stun3.l.google.com:19302' },
                { urls: 'stun:stun4.l.google.com:19302' },
                { urls: 'stun:stun.cloudflare.com:3478' },
                { urls: 'stun:global.stun.twilio.com:3478' }
            ],
            iceCandidatePoolSize: 10,
            bundlePolicy: 'max-bundle',
            rtcpMuxPolicy: 'require'
        };
        
        const pc = new RTCPeerConnection(servers);
        pcRef.current = pc;
        addLog('RTCPeerConnection instance created with high-performance multi-STUN configuration.', 'info');

        // Track Signaling State
        pc.onsignalingstatechange = () => {
            addLog(`Signaling state changed to: ${pc.signalingState}`, 'info');
            setWebrtcStats(prev => ({ ...prev, signalingState: pc.signalingState }));
        };

        // Track ICE Connection State & Automatic Healing
        pc.oniceconnectionstatechange = () => {
            const state = pc.iceConnectionState;
            addLog(`ICE connection state: ${state}`, state === 'connected' || state === 'completed' ? 'success' : state === 'failed' ? 'error' : 'info');
            setWebrtcStats(prev => ({ ...prev, iceConnectionState: state }));
            if (state === 'failed') {
                try {
                    if (typeof (pc as any).restartIce === 'function') {
                        addLog('Attempting automatic ICE restart for link recovery...', 'info');
                        (pc as any).restartIce();
                    }
                } catch (_) {}
            }
        };

        // --- IN-CALL REALTIME CHAT SUBSCRIPTION ---
        const chatUnsub = onSnapshot(query(collection(db, 'calls', appointmentId, 'messages'), orderBy('timestamp', 'asc')), (snapshot) => {
            const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
            setChatMessages(msgs);
        }, (err) => {
            console.debug("In-call chat sync offline/pending:", err);
        });
        unsubsRef.current.push(chatUnsub);
        
        // Add all local tracks with performance encoding constraints
        s.getTracks().forEach((track) => {
            const sender = pc.addTrack(track, s);
            if (track.kind === 'video' && sender) {
                try {
                    const params = sender.getParameters();
                    if (params) {
                        if (!params.encodings || params.encodings.length === 0) {
                            params.encodings = [{}];
                        }
                        params.encodings[0].maxBitrate = 1_800_000; // 1.8 Mbps bitrate cap prevents bufferbloat
                        params.encodings[0].networkPriority = 'high';
                        params.degradationPreference = 'maintain-framerate'; // Smooth fluid video during network fluctuations
                        sender.setParameters(params).catch(() => {});
                    }
                } catch (_) {}
            }
            addLog(`Attached local ${track.kind} track (enabled: ${track.enabled})`, 'info');
        });
        
        // Listen for incoming remote tracks
        pc.ontrack = (event) => {
            addLog(`Received remote track (${event.track.kind})! Streams count: ${event.streams.length}`, 'success');
            if (event.streams && event.streams[0]) {
                setWebrtcStats(prev => ({ ...prev, remoteTracksCount: event.streams[0].getTracks().length }));
                handleRemoteStream(event.streams[0]);
            }
        };
        
        // Monitor Peer Connection State
        pc.onconnectionstatechange = () => {
            const state = pc.connectionState;
            addLog(`Peer connection state: ${state}`, state === 'connected' ? 'success' : state === 'failed' ? 'error' : 'info');
            setWebrtcStats(prev => ({ ...prev, connectionState: state }));

            if (state === 'connected') {
                setPeerStatus('connected');
                setDiagnostic('Consultation Connected');
                updateDoc(callDocRef, { bothConnected: true, inProgress: true, lastConnectedAt: Date.now() }).catch(() => {});
            } else if (state === 'disconnected') {
                setPeerStatus('disconnected');
                setDiagnostic(isDoctor ? `Connection fluctuating. Reconnecting with ${displayPatientName}...` : `Connection fluctuating. Reconnecting with ${displayDoctorName}...`);
            } else if (state === 'failed') {
                setPeerStatus('failed');
                setDiagnostic(isDoctor ? `Connection interrupted. Waiting or click Reconnect.` : `Connection interrupted. Waiting or click Reconnect.`);
                updateDoc(callDocRef, { bothConnected: false }).catch(() => {});
            }
        };

        const callDocRef = doc(db, 'calls', appointmentId);
        const offerCandidates = collection(callDocRef, 'offerCandidates');
        const answerCandidates = collection(callDocRef, 'answerCandidates');
        
        if (isDoctor) {
            adminUpdateAppointment(appointmentId, { status: 'in-progress' });
            
            const newSessionId = `${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
            currentSessionIdRef.current = newSessionId;
            addLog(`Doctor generating new session ID: ${newSessionId}`, 'info');

            pc.onicecandidate = (event) => {
                if (event.candidate) {
                    setWebrtcStats(prev => ({ ...prev, localCandidatesCount: prev.localCandidatesCount + 1 }));
                    addDoc(offerCandidates, {
                        candidate: event.candidate.toJSON(),
                        sessionId: newSessionId,
                        timestamp: Date.now()
                    }).catch(err => console.debug('ICE offer candidate add debug:', err));
                }
            };
            
            const offerDescription = await pc.createOffer({
                offerToReceiveAudio: true,
                offerToReceiveVideo: true
            });
            await pc.setLocalDescription(offerDescription);
            addLog('Doctor created and set local SDP offer.', 'info');
            
            const offer = {
                sdp: offerDescription.sdp,
                type: offerDescription.type,
                sessionId: newSessionId
            };
            
            const existingSnap = await getDoc(callDocRef).catch(() => null);
            const existingData = existingSnap && existingSnap.exists() ? existingSnap.data() : null;
            const initialCallStartedAt = existingData?.callStartedAt || Date.now();
            const initialDuration = existingData?.totalDurationSeconds || 1800;
            callStartedAtRef.current = initialCallStartedAt;
            totalDurationRef.current = initialDuration;
            
            if (typeof window !== 'undefined') {
                try {
                    localStorage.removeItem(`rovita_ended_call_${appointmentId}`);
                    if ('BroadcastChannel' in window) {
                        const bc = new BroadcastChannel('rovita_call_sync_channel');
                        bc.postMessage({ type: 'CALL_STARTED', appointmentId });
                        bc.close();
                    }
                } catch (e) {}
            }

            await setDoc(callDocRef, { 
                offer, 
                doctorId: user.id, 
                patientId: appointment?.patientId || '',
                doctorOnline: true,
                doctorJoinedAt: Date.now(),
                currentSessionId: newSessionId,
                callStartedAt: initialCallStartedAt,
                totalDurationSeconds: initialDuration,
                status: 'in-progress',
                inProgress: true,
                callEnded: false,
                endedAt: null,
                endedBy: null,
                endedByName: null
            }, { merge: true });
            addLog('Published SDP offer to Firestore room document.', 'info');
            
            setPeerStatus('waiting');
            setDiagnostic(`Room ready. Waiting for ${displayPatientName} to join...`);
            
            const unsubCall = onSnapshot(callDocRef, async (snapshot) => {
                const data = snapshot.data();
                if (data && (data.callEnded === true || data.status === 'ended')) {
                    addLog('Consultation was concluded by patient.', 'info');
                    setCallEndedByRemote(data.endedByName || displayPatientName || 'Patient');
                    setPeerStatus('disconnected');
                    cleanupMedia();
                    return;
                }
                if (data) {
                    if (data.callStartedAt && typeof data.callStartedAt === 'number') {
                        callStartedAtRef.current = data.callStartedAt;
                        const duration = data.totalDurationSeconds || 1800;
                        totalDurationRef.current = duration;
                        const elapsed = Math.max(0, Math.floor((Date.now() - data.callStartedAt) / 1000));
                        setSessionSecondsRemaining(Math.max(0, duration - elapsed));
                        setTotalSessionDuration(duration);
                    }
                }
                if (data && data.answer && data.answer.sessionId === newSessionId) {
                    if (!pc.currentRemoteDescription && pc.signalingState !== 'stable') {
                        try {
                            addLog('Received patient SDP answer. Setting remote description...', 'info');
                            const answerDescription = new RTCSessionDescription(data.answer);
                            await pc.setRemoteDescription(answerDescription);
                            await drainQueuedCandidates(pc);
                            addLog('Patient SDP answer applied successfully!', 'success');
                            setDiagnostic(`Connected with ${displayPatientName}`);
                        } catch (e: any) {
                            addLog(`Doctor remote desc error: ${e?.message || e}`, 'warn');
                            console.debug("Doctor remote desc apply debug:", e);
                        }
                    }
                }
                if (data && data.patientOnline) {
                    setIsRemoteParticipantInRoom(true);
                    setShowPatientNoResponseModal(false);
                    setDoctorWaitingSeconds(0);
                    addLog(`${displayPatientName} signaled online in room.`, 'info');
                    setDiagnostic(`${displayPatientName} is in the room. Establishing connection...`);
                }
            }, (err) => {
                console.debug("Doctor call document sync offline/pending:", err);
            });
            
            const unsubAns = onSnapshot(answerCandidates, (snapshot) => {
                snapshot.docChanges().forEach(async (change) => {
                    if (change.type === 'added') {
                        const candData = change.doc.data();
                        if (candData.sessionId === newSessionId && candData.candidate) {
                            setWebrtcStats(prev => ({ ...prev, remoteCandidatesCount: prev.remoteCandidatesCount + 1 }));
                            if (pc.remoteDescription && pc.remoteDescription.type) {
                                try {
                                    await pc.addIceCandidate(new RTCIceCandidate(candData.candidate));
                                } catch (e) {
                                    console.debug("Doctor ICE candidate apply error:", e);
                                }
                            } else {
                                remoteCandidatesQueue.current.push(candData.candidate);
                            }
                        }
                    }
                });
            }, (err) => {
                console.debug("Answer candidate sync offline/pending:", err);
            });
            unsubsRef.current.push(unsubCall, unsubAns);
        } else {
            // Patient Participant Flow
            adminUpdateAppointment(appointmentId, { status: 'in-progress' });
            addLog('Patient participant joined. Waiting for doctor SDP offer...', 'info');
            
            pc.onicecandidate = (event) => {
                if (event.candidate && currentSessionIdRef.current) {
                    setWebrtcStats(prev => ({ ...prev, localCandidatesCount: prev.localCandidatesCount + 1 }));
                    addDoc(answerCandidates, {
                        candidate: event.candidate.toJSON(),
                        sessionId: currentSessionIdRef.current,
                        timestamp: Date.now()
                    }).catch(err => console.debug('ICE answer candidate add debug:', err));
                }
            };
            
            let handledSessionId = '';
            setPeerStatus('waiting');
            setDiagnostic(`Room ready. Waiting for ${displayDoctorName} to join...`);
            
            const patientExistingSnap = await getDoc(callDocRef).catch(() => null);
            const patientExistingData = patientExistingSnap && patientExistingSnap.exists() ? patientExistingSnap.data() : null;
            const patientCallStartedAt = patientExistingData?.callStartedAt || Date.now();
            const patientDuration = patientExistingData?.totalDurationSeconds || 1800;
            callStartedAtRef.current = patientCallStartedAt;
            totalDurationRef.current = patientDuration;

            if (typeof window !== 'undefined') {
                try {
                    localStorage.removeItem(`rovita_ended_call_${appointmentId}`);
                    if ('BroadcastChannel' in window) {
                        const bc = new BroadcastChannel('rovita_call_sync_channel');
                        bc.postMessage({ type: 'CALL_STARTED', appointmentId });
                        bc.close();
                    }
                } catch (e) {}
            }

            await setDoc(callDocRef, {
                patientOnline: true,
                patientJoinedAt: Date.now(),
                patientId: user.id,
                doctorId: appointment?.doctorId || '',
                callStartedAt: patientCallStartedAt,
                totalDurationSeconds: patientDuration,
                status: 'in-progress',
                inProgress: true,
                callEnded: false,
                endedAt: null,
                endedBy: null,
                endedByName: null
            }, { merge: true });
            addLog('Patient marked online in Firestore call document.', 'info');

            const unsubCall = onSnapshot(callDocRef, async (snapshot) => {
                const data = snapshot.data();
                if (data && (data.callEnded === true || data.status === 'ended' || data.status === 'completed')) {
                    addLog('Consultation was concluded by doctor.', 'info');
                    setCallEndedByRemote(data.endedByName || displayDoctorName || 'Doctor');
                    setPeerStatus('disconnected');
                    cleanupMedia();
                    return;
                }
                if (data) {
                    if (data.callStartedAt && typeof data.callStartedAt === 'number') {
                        callStartedAtRef.current = data.callStartedAt;
                        const duration = data.totalDurationSeconds || 1800;
                        totalDurationRef.current = duration;
                        const elapsed = Math.max(0, Math.floor((Date.now() - data.callStartedAt) / 1000));
                        setSessionSecondsRemaining(Math.max(0, duration - elapsed));
                        setTotalSessionDuration(duration);
                    }
                }
                if (data && data.offer && data.offer.sessionId && data.offer.sessionId !== handledSessionId) {
                    handledSessionId = data.offer.sessionId;
                    currentSessionIdRef.current = data.offer.sessionId;
                    addLog(`Detected Doctor's SDP offer (Session: ${data.offer.sessionId}). Applying...`, 'info');
                    setDiagnostic(`${displayDoctorName} is in the room. Connecting audio & video...`);

                    try {
                        const offerDescription = new RTCSessionDescription(data.offer);
                        await pc.setRemoteDescription(offerDescription);
                        await drainQueuedCandidates(pc);
                        addLog('Remote Doctor offer set. Creating SDP answer...', 'info');
                        
                        const answerDescription = await pc.createAnswer();
                        await pc.setLocalDescription(answerDescription);
                        addLog('Local SDP answer created and set.', 'info');
                        
                        const answer = {
                            type: answerDescription.type,
                            sdp: answerDescription.sdp,
                            sessionId: data.offer.sessionId
                        };
                        
                        await updateDoc(callDocRef, { 
                            answer,
                            patientOnline: true,
                            patientLastSeen: Date.now()
                        });
                        addLog('SDP answer published to Firestore.', 'success');
                    } catch (e: any) {
                        addLog(`Patient answer error: ${e?.message || e}`, 'warn');
                        console.debug("Patient answer signaling debug:", e);
                    }
                }
                if (data && data.doctorOnline) {
                    setIsRemoteParticipantInRoom(true);
                    addLog(`${displayDoctorName} is active in the room.`, 'info');
                    setDiagnostic(`${displayDoctorName} is active in the room.`);
                }
            }, (err) => {
                console.debug("Patient call document sync offline/pending:", err);
            });
            
            const unsubOffer = onSnapshot(offerCandidates, (snapshot) => {
                snapshot.docChanges().forEach(async (change) => {
                    if (change.type === 'added') {
                        const candData = change.doc.data();
                        if (candData.candidate && (!candData.sessionId || candData.sessionId === currentSessionIdRef.current)) {
                            setWebrtcStats(prev => ({ ...prev, remoteCandidatesCount: prev.remoteCandidatesCount + 1 }));
                            if (pc.remoteDescription && pc.remoteDescription.type) {
                                try {
                                    await pc.addIceCandidate(new RTCIceCandidate(candData.candidate));
                                } catch (e) {
                                    console.debug("Patient ICE candidate apply error:", e);
                                }
                            } else {
                                remoteCandidatesQueue.current.push(candData.candidate);
                            }
                        }
                    }
                });
            }, (err) => {
                console.debug("Offer candidate sync offline/pending:", err);
            });
            unsubsRef.current.push(unsubCall, unsubOffer);
        }
    };

    const startMedia = async () => {
        setErrorMsg(null);
        setPeerStatus('hardware_init');
        setDiagnostic('Connecting camera & microphone...');
        
        let s: MediaStream | null = null;
        const constraintTiers: MediaStreamConstraints[] = [
            // Tier 1: Wide full-angle front camera with HD resolution, fluid 24-30 fps, and studio-grade audio
            {
                video: {
                    facingMode: 'user',
                    width: { ideal: isPortraitMode ? 720 : 1280, max: 1280 },
                    height: { ideal: isPortraitMode ? 1280 : 720, max: 720 },
                    frameRate: { ideal: 24, max: 30 }
                },
                audio: { 
                    echoCancellation: true, 
                    noiseSuppression: true, 
                    autoGainControl: true,
                    sampleRate: 48000,
                    channelCount: 1
                }
            },
            // Tier 2: Standard mobile front camera (unconstrained aspect ratio)
            {
                video: { 
                    facingMode: 'user',
                    frameRate: { ideal: 24, max: 30 }
                },
                audio: { 
                    echoCancellation: true, 
                    noiseSuppression: true, 
                    autoGainControl: true 
                }
            },
            // Tier 3: Any available video + audio
            {
                video: { frameRate: { ideal: 24, max: 30 } },
                audio: true
            },
            // Tier 4: Audio-only fallback if video camera is unavailable or strictly restricted
            {
                video: false,
                audio: true
            }
        ];

        for (const tier of constraintTiers) {
            try {
                s = await navigator.mediaDevices.getUserMedia(tier);
                if (s) {
                    // Attempt to reset any camera hardware/driver digital zoom to standard wide 1.0x if supported
                    try {
                        const vTrack = s.getVideoTracks()[0];
                        if (vTrack && typeof (vTrack as any).getCapabilities === 'function') {
                            const capabilities = (vTrack as any).getCapabilities();
                            if (capabilities && 'zoom' in capabilities) {
                                const minZoom = capabilities.zoom.min || 1;
                                const maxZoom = capabilities.zoom.max || 3;
                                const step = capabilities.zoom.step || 0.1;
                                setHardwareZoomCapabilities({ min: minZoom, max: maxZoom, step });
                                setCurrentHardwareZoom(minZoom);
                                await (vTrack as any).applyConstraints({
                                    advanced: [{ zoom: minZoom }]
                                }).catch(() => {});
                            }
                        }
                    } catch (_) {}
                    break;
                }
            } catch (tierErr) {
                console.warn("Retrying media constraints tier fallback:", tierErr);
            }
        }

        if (!s) {
            setPeerStatus('failed');
            setErrorMsg("We couldn't access your camera or microphone. Please verify device permissions in your mobile or browser settings.");
            return;
        }

        try {
            localStreamRef.current = s;
            setStream(s);
            
            if (videoRef.current) {
                videoRef.current.srcObject = s;
                videoRef.current.play().catch(() => {});
            }
            setDiagnostic('Camera and microphone connected. Preparing consultation room...');
        } catch (e: any) {
            console.error("Media Error:", e);
            setPeerStatus('failed');
            setErrorMsg("We couldn't access your camera or microphone. Please check your browser permissions.");
            return;
        }

        if (user && s) {
            setPeerStatus('signaling');
            setDiagnostic('Connecting to your private consultation room...');
            
            try {
                await initWebRTC(s);
            } catch (initError) {
                console.error("WebRTC Init Error:", initError);
                setPeerStatus('failed');
                setErrorMsg("Unable to connect to the room. Please refresh the page.");
            }
        }
    };

    const initializationAttempted = React.useRef(false);
    React.useEffect(() => {
        if (initializationAttempted.current) return;
        initializationAttempted.current = true;
        
        startMedia();
        if (isDoctor && appointment) {
            adminUpdateAppointment(appointmentId, { startTime: new Date().toISOString() });
        }
        return () => {
            cleanupMedia();
            if (typeof window !== 'undefined') {
                try {
                    const raw = localStorage.getItem('rovita_active_call_session');
                    if (raw) {
                        try {
                            const parsed = JSON.parse(raw);
                            if (parsed.appointmentId === appointmentId) {
                                localStorage.removeItem('rovita_active_call_session');
                            }
                        } catch (_) {}
                    }
                    if ('BroadcastChannel' in window) {
                        const bc = new BroadcastChannel('rovita_call_sync_channel');
                        bc.postMessage({ type: 'CALL_ENDED', appointmentId });
                        bc.close();
                    }
                } catch (_) {}
            }
            try {
                const db = getFirestore(getApp());
                const callDocRef = doc(db, 'calls', appointmentId);
                updateDoc(callDocRef, {
                    [isDoctor ? 'doctorOnline' : 'patientOnline']: false,
                    bothConnected: false
                }).catch(() => {});
            } catch (_) {}
        };
    }, []);

    // Auto-save doctor private notes to appointment document
    React.useEffect(() => {
        if (!privateNotes || !appointmentId || !isDoctor) return;
        const db = getFirestore(getApp());
        const timeout = setTimeout(() => {
            updateDoc(doc(db, 'appointments', appointmentId), { privateNotes: privateNotes }).catch(() => {});
        }, 3000);
        return () => clearTimeout(timeout);
    }, [privateNotes, appointmentId, isDoctor]);

    // Submit Complete Medical Record & Finalize
    const submitRecord = async () => {
        if (!appointment) return;
        setIsUploading(true);
        try {
            const endTime = new Date().toISOString();
            
            let uploadedFiles: { name: string; url: string; size?: number; type?: string }[] = [];
            if (selectedAttachments.length > 0) {
                try {
                    const results = await Promise.all(selectedAttachments.map(f => uploadClinicalFile(f)));
                    uploadedFiles = results.map((r, i) => ({
                        name: r.name,
                        url: r.url,
                        size: selectedAttachments[i]?.size,
                        type: selectedAttachments[i]?.type
                    }));
                } catch (uploadErr) {
                    console.warn("Attachment upload warning:", uploadErr);
                }
            }

            const newRecord: Omit<MedicalRecord, 'id'> = {
                patientId: appointment.patientId,
                doctorId: appointment.doctorId,
                doctorName: appointment.doctorName,
                date: getTodayDateString(),
                authoredBy: 'doctor',
                
                presentingSymptoms: recordData.presentingSymptoms,
                historyOfPresentIllness: recordData.history,
                pastMedicalHistory: recordData.pastHistory,
                socialHistory: recordData.socialHistory,
                vitalSigns: recordData.vitals,
                physicalExamFindings: recordData.physicalExam,
                labReports: recordData.labReports,
                imagingStudies: recordData.imaging,
                
                diagnosis: recordData.diagnosis || 'Clinical Consultation',
                treatmentPlan: recordData.treatmentPlan,
                prescription: recordData.prescription,
                followUpInstructions: recordData.followUp,
                referralsIssued: recordData.referrals,
                notes: recordData.notes,
                privateNotes: privateNotes,

                attachmentUrl: uploadedFiles[0]?.url,
                attachmentName: uploadedFiles[0]?.name,
                attachments: uploadedFiles.length > 0 ? uploadedFiles : undefined,

                chatTranscript: chatMessages && chatMessages.length > 0 ? chatMessages.map(m => ({
                    id: m.id || `${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
                    senderId: m.senderId,
                    senderName: m.senderName,
                    text: m.text,
                    timestamp: m.timestamp
                })) : undefined
            };

            await addMedicalRecord(newRecord);
            if (typeof window !== 'undefined' && appointmentId) {
                try {
                    localStorage.removeItem(`rovita_consultation_draft_${appointmentId}`);
                } catch (e) {}
            }
            await adminUpdateAppointment(appointmentId, { 
                status: 'completed',
                endTime: endTime
            });
            await handleEndSession(true);
        } catch (err: any) {
            console.error("Error submitting record:", err);
            await handleEndSession(false);
        } finally {
            setIsUploading(false);
        }
    };

    // Toggle Audio/Video
    const toggleAudio = () => {
        if (localStreamRef.current) {
            localStreamRef.current.getAudioTracks().forEach(track => track.enabled = !track.enabled);
            setIsMuted(!isMuted);
        }
    };

    const toggleVideo = () => {
        if (localStreamRef.current) {
            localStreamRef.current.getVideoTracks().forEach(track => track.enabled = !track.enabled);
            setIsVideoOff(!isVideoOff);
        }
    };

    const sendMessage = async () => {
        if (!newMessage.trim() || !user) return;
        const db = getFirestore(getApp());
        await addDoc(collection(db, 'calls', appointmentId, 'messages'), {
            senderId: user.id,
            senderName: user.name,
            text: newMessage.trim(),
            timestamp: new Date().toISOString()
        });
        setNewMessage('');
    };

    const patientHistory = React.useMemo(() => {
        return medicalRecords
            .filter(r => r.patientId === appointment?.patientId)
            .sort((a, b) => parseRecordTimestamp(b) - parseRecordTimestamp(a));
    }, [medicalRecords, appointment?.patientId]);
    
    // Find any digital prescription created in this consultation or active for this appointment
    const latestPrescriptionRecord = React.useMemo(() => {
        return medicalRecords.find(r => 
            r.patientId === appointment?.patientId && 
            (r.appointmentId === appointmentId || (issuedPrescriptionRxId && r.rxId === issuedPrescriptionRxId))
        ) || null;
    }, [medicalRecords, appointment?.patientId, appointmentId, issuedPrescriptionRxId]);

    // Find all digital prescriptions for this patient
    const allPatientPrescriptions = React.useMemo(() => {
        return medicalRecords
            .filter(r => r.patientId === appointment?.patientId && (r.prescription || r.rxId || (r.prescriptionItems && r.prescriptionItems.length > 0)))
            .sort((a, b) => parseRecordTimestamp(b) - parseRecordTimestamp(a));
    }, [medicalRecords, appointment?.patientId]);

    return (
        <div className="fixed inset-0 bg-slate-950 z-[200] flex flex-col font-['Plus_Jakarta_Sans'] text-white">
            {/* Clinical Top Navigation Bar */}
            <div className="p-4 md:p-6 bg-slate-900 border-b border-white/5 flex justify-between items-center h-20 md:h-24">
                <div className="flex items-center gap-4 md:gap-6">
                    <button 
                        onClick={() => setShowPatientPanel(!showPatientPanel)} 
                        aria-label={showPatientPanel ? "Close clinical information panel" : "Open clinical information panel"}
                        aria-expanded={showPatientPanel}
                        className={`w-10 h-10 md:w-12 md:h-12 rounded-2xl flex items-center justify-center shadow-lg hover:scale-110 transition-transform ${showPatientPanel ? 'bg-slate-700' : 'bg-indigo-600'}`}
                    >
                        <i className={`fa-solid ${showPatientPanel ? 'fa-video' : (isDoctor ? 'fa-clipboard-user' : 'fa-comments')}`} aria-hidden="true"></i>
                    </button>
                    <div>
                        <p className="text-[8px] font-black text-indigo-400 uppercase tracking-widest mb-0.5">{isDoctor ? 'Patient Clinical Profile' : 'Consultation Room'}</p>
                        <h2 className="font-black text-sm md:text-xl truncate max-w-[150px] md:max-w-none">{isDoctor ? `Patient: ${displayPatientName}` : displayDoctorName}</h2>
                    </div>
                </div>
                <div className="flex items-center gap-2 md:gap-6">
                    {/* Visual Countdown Timer Badge */}
                    <div className={`px-3 py-1.5 md:px-4 md:py-2 rounded-2xl border flex items-center gap-2.5 transition-all shadow-md ${
                        sessionSecondsRemaining <= 60 
                            ? 'bg-rose-500/20 text-rose-300 border-rose-500/50 animate-pulse ring-2 ring-rose-500/30' 
                            : sessionSecondsRemaining <= 300 
                            ? 'bg-amber-500/20 text-amber-300 border-amber-500/50' 
                            : 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
                    }`}>
                        <i className={`fa-solid ${sessionSecondsRemaining <= 300 ? 'fa-clock-rotate-left text-amber-400 animate-spin' : 'fa-hourglass-half text-emerald-400'}`}></i>
                        <div className="flex flex-col">
                            <span className="text-[7px] md:text-[8px] font-black uppercase tracking-widest text-slate-400">Session Timer</span>
                            <span className="text-xs md:text-sm font-mono font-black tracking-wider">{formatTimer(sessionSecondsRemaining)}</span>
                        </div>
                    </div>

                    {/* Extension button for doctor */}
                    {isDoctor && (
                        <button 
                            onClick={() => handleExtendSession(5)}
                            title="Add 5 Minutes to Session Duration"
                            className="hidden sm:flex items-center gap-1 px-3 py-2 bg-indigo-950/80 hover:bg-indigo-900 text-indigo-300 border border-indigo-700/60 rounded-xl text-xs font-bold transition-all active:scale-95 shadow-sm"
                        >
                            <i className="fa-solid fa-plus text-[10px]"></i> 5m
                        </button>
                    )}

                    <div className="hidden md:block text-right">
                        <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Meeting Start</p>
                        <p className="text-xs font-black">{new Date(startTime).toLocaleTimeString()}</p>
                    </div>

                    <div className={`px-3 py-1.5 md:px-5 md:py-2.5 rounded-full text-ui-tiny md:text-ui-small font-black uppercase border flex items-center gap-2 md:gap-3 ${peerStatus === 'connected' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'}`}>
                        <span className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full ${peerStatus === 'connected' ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`}></span>
                        {peerStatus === 'connected' ? 'Connected' : 'Connecting...'}
                    </div>

                    {/* Post-Consultation Form button for doctor */}
                    {isDoctor && (
                        <button 
                            onClick={() => setShowRecordForm(true)}
                            title="Complete Post-Consultation Clinical Summary & e-Rx"
                            className="flex items-center gap-1.5 px-3 py-1.5 md:px-4 md:py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl text-xs font-bold transition-all shadow-md shadow-indigo-600/30 active:scale-95"
                        >
                            <i className="fa-solid fa-file-medical text-xs"></i>
                            <span className="hidden sm:inline">Clinical Summary Form</span>
                        </button>
                    )}

                    <button
                        onClick={() => isDoctor ? setShowRecordForm(true) : handleEndSession()}
                        title="Leave Consultation Room"
                        aria-label="Leave Consultation Room"
                        className="px-3 py-1.5 md:px-4 md:py-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/30 rounded-2xl text-xs font-bold transition-all flex items-center gap-2 active:scale-95"
                    >
                        <i className="fa-solid fa-arrow-right-from-bracket" aria-hidden="true"></i>
                        <span className="hidden sm:inline">Leave Room</span>
                    </button>
                </div>
            </div>

            {/* Video Stage Container */}
            <div className="flex-grow flex relative bg-black overflow-hidden select-none">
                {/* Main View Area */}
                <div 
                    ref={mainViewRef} 
                    onClick={() => {
                        if (isAudioAutoplayBlocked && remoteVideoRef.current) {
                            remoteVideoRef.current.play().then(() => setIsAudioAutoplayBlocked(false)).catch(() => {});
                        }
                    }}
                    className={`flex-grow relative flex items-center justify-center transition-all duration-500 ${showPatientPanel ? 'md:mr-[30%]' : ''}`}
                >
                    <video 
                        ref={remoteVideoRef} 
                        autoPlay 
                        playsInline 
                        onLoadedMetadata={(e) => {
                            const v = e.currentTarget;
                            if (v && v.videoHeight > v.videoWidth) {
                                setRemoteVideoFit('contain');
                            }
                        }}
                        onDoubleClick={() => setRemoteVideoFit(prev => prev === 'cover' ? 'contain' : 'cover')}
                        style={{ transform: `scale(${remoteScale})` }}
                        className={`w-full h-full z-10 ${remoteVideoFit === 'contain' ? 'object-contain' : 'object-cover'} transition-all duration-300 ${remoteStream ? 'opacity-100' : 'opacity-0'}`} 
                    />

                    {/* Audio Autoplay Unblock Banner if Browser Blocks Audio */}
                    {isAudioAutoplayBlocked && (
                        <div 
                            onClick={(e) => {
                                e.stopPropagation();
                                if (remoteVideoRef.current) {
                                    remoteVideoRef.current.play().then(() => setIsAudioAutoplayBlocked(false)).catch(() => {});
                                }
                            }}
                            className="absolute top-20 left-1/2 -translate-x-1/2 z-50 bg-teal-600 hover:bg-teal-500 text-white px-5 py-2.5 rounded-full shadow-2xl flex items-center gap-2.5 cursor-pointer text-xs font-bold animate-bounce border border-teal-300/40"
                        >
                            <i className="fa-solid fa-volume-high text-sm"></i>
                            <span>Tap here to enable audio from {isDoctor ? displayPatientName : displayDoctorName}</span>
                        </div>
                    )}
                    
                    {/* Floating Zoom & Framing Toolbar for Doctor / Remote View */}
                    {remoteStream && (
                        <div className="absolute bottom-20 left-4 z-20 flex flex-wrap items-center gap-1.5 p-1 bg-slate-900/90 hover:bg-slate-900 backdrop-blur-md rounded-2xl border border-white/20 shadow-2xl transition-all font-['Plus_Jakarta_Sans']">
                            <button
                                onClick={() => {
                                    setRemoteVideoFit(prev => prev === 'cover' ? 'contain' : 'cover');
                                    setRemoteScale(1);
                                }}
                                type="button"
                                title={remoteVideoFit === 'cover' ? "Zoom out to view 100% full uncropped camera (Fit to screen)" : "Fill screen with video"}
                                aria-label="Toggle camera framing fit"
                                className={`px-2.5 py-1.5 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-all active:scale-95 ${
                                    remoteVideoFit === 'contain' 
                                        ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40' 
                                        : 'bg-white/10 text-white hover:bg-white/20'
                                }`}
                            >
                                <i className={`fa-solid ${remoteVideoFit === 'cover' ? 'fa-compress' : 'fa-expand'} text-teal-400 text-xs`}></i>
                                <span>{remoteVideoFit === 'contain' ? 'Full View (Uncropped)' : 'Fill Screen'}</span>
                            </button>

                            <div className="h-4 w-px bg-white/20 mx-0.5"></div>

                            {/* Zoom Out / Zoom In Step Buttons */}
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setRemoteScale(s => Math.max(0.6, Number((s - 0.15).toFixed(2))))}
                                    title="Zoom Out Video (Wide Angle)"
                                    aria-label="Zoom Out Video"
                                    className="w-7 h-7 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-xs font-bold transition-all active:scale-90"
                                >
                                    <i className="fa-solid fa-minus"></i>
                                </button>
                                
                                <button
                                    onClick={() => setRemoteScale(1)}
                                    title="Reset Zoom to 1.0x"
                                    className="px-2 py-1 rounded-lg bg-white/5 text-[10px] font-mono font-bold text-slate-300 hover:text-white"
                                >
                                    {Math.round(remoteScale * 100)}%
                                </button>

                                <button
                                    onClick={() => setRemoteScale(s => Math.min(2.5, Number((s + 0.15).toFixed(2))))}
                                    title="Zoom In Video"
                                    aria-label="Zoom In Video"
                                    className="w-7 h-7 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-xs font-bold transition-all active:scale-90"
                                >
                                    <i className="fa-solid fa-plus"></i>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Floating Doctor Waiting & Milestone Banner (Doctor Only, while waiting) */}
                    {isDoctor && !remoteStream && (
                        <div className="absolute top-4 inset-x-4 max-w-xl mx-auto z-30 bg-slate-900/95 backdrop-blur-md border border-slate-700/80 rounded-2xl p-3.5 shadow-2xl animate-in fade-in slide-in-from-top-3 duration-300 font-['Plus_Jakarta_Sans']">
                            <div className="flex items-center justify-between gap-3 mb-2">
                                <div className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-full bg-teal-400 animate-pulse"></span>
                                    <span className="text-xs font-black text-white uppercase tracking-wider">Waiting for Patient to Connect</span>
                                </div>
                                <span className="font-mono text-xs font-black text-teal-300 bg-teal-950/80 px-2.5 py-0.5 rounded-lg border border-teal-500/40 shadow-sm">
                                    {Math.floor(doctorWaitingSeconds / 60)}:{(doctorWaitingSeconds % 60).toString().padStart(2, '0')} / 02:00
                                </span>
                            </div>
                            
                            {/* Progress Bar */}
                            <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden mb-2.5">
                                <div 
                                    className={`h-full transition-all duration-1000 ${doctorWaitingSeconds >= 30 ? 'bg-teal-400' : 'bg-indigo-400'}`}
                                    style={{ width: `${Math.min(100, (doctorWaitingSeconds / 120) * 100)}%` }}
                                ></div>
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-300">
                                <div className="flex items-center gap-1.5">
                                    <i className={`fa-solid fa-envelope-circle-check text-xs ${doctorWaitingSeconds >= 30 ? 'text-emerald-400' : 'text-slate-500'}`}></i>
                                    <span>
                                        Email reminder: {doctorWaitingSeconds >= 30 ? <strong className="text-emerald-300 font-bold">Sent to patient</strong> : <span className="text-slate-400">Sends automatically at 0:30</span>}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1.5 text-slate-400">
                                    <i className="fa-solid fa-clock-rotate-left text-xs text-indigo-400"></i>
                                    <span>Closes after 2:00 if patient cannot join</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Real-time In-Call e-Prescription Banner (Dismissible & Hidable) */}
                    {(latestPrescriptionRecord || issuedPrescriptionRxId) && !isPrescriptionBannerDismissed && (
                        <div className={`absolute ${isDoctor && !remoteStream ? 'top-28' : 'top-4'} left-1/2 -translate-x-1/2 z-30 bg-slate-900/90 backdrop-blur-md border border-emerald-500/40 shadow-2xl rounded-2xl px-4 py-2.5 flex items-center gap-3 animate-in fade-in slide-in-from-top-4 duration-300 font-['Plus_Jakarta_Sans']`}>
                            <div className="w-8 h-8 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center shrink-0">
                                <i className="fa-solid fa-prescription-bottle-medical text-sm"></i>
                            </div>
                            <div className="text-left">
                                <p className="text-[9px] font-black uppercase tracking-wider text-emerald-400">Digital Prescription Issued</p>
                                <p className="text-xs font-bold text-white">
                                    {latestPrescriptionRecord?.rxId || issuedPrescriptionRxId ? `Rx #${latestPrescriptionRecord?.rxId || issuedPrescriptionRxId}` : 'Prescription Available in Vault'}
                                </p>
                            </div>
                            <div className="flex items-center gap-1.5 ml-1">
                                <button
                                    onClick={() => {
                                        if (latestPrescriptionRecord) {
                                            setViewingPrescriptionRecord(latestPrescriptionRecord);
                                        } else {
                                            const rec = medicalRecords.find(r => r.rxId === issuedPrescriptionRxId);
                                            if (rec) setViewingPrescriptionRecord(rec);
                                            else if (isDoctor) setShowPrescriptionGenerator(true);
                                        }
                                    }}
                                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold transition-all shadow-md active:scale-95 flex items-center gap-1.5"
                                >
                                    <i className="fa-solid fa-file-prescription"></i>
                                    <span>View & Print Rx</span>
                                </button>
                                {isDoctor && (
                                    <button
                                        onClick={() => setShowPrescriptionGenerator(true)}
                                        className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold transition-all shadow-md active:scale-95 flex items-center gap-1.5"
                                        title="Write Another New Prescription"
                                    >
                                        <i className="fa-solid fa-plus text-[10px]"></i>
                                        <span>New Rx</span>
                                    </button>
                                )}
                                <button
                                    onClick={() => setIsPrescriptionBannerDismissed(true)}
                                    title="Hide prescription notice"
                                    aria-label="Hide prescription notice"
                                    className="w-7 h-7 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center text-xs transition-all active:scale-90"
                                >
                                    <i className="fa-solid fa-xmark"></i>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Small Unobtrusive Indicator Pill if Prescription is Dismissed */}
                    {(latestPrescriptionRecord || issuedPrescriptionRxId) && isPrescriptionBannerDismissed && (
                        <button
                            onClick={() => setIsPrescriptionBannerDismissed(false)}
                            title="Show prescription notice"
                            className="absolute top-4 right-4 z-20 bg-slate-900/85 hover:bg-slate-800 backdrop-blur-md border border-emerald-500/40 text-emerald-300 rounded-full px-3 py-1.5 text-xs font-semibold shadow-xl flex items-center gap-1.5 transition-all active:scale-95 animate-in fade-in"
                        >
                            <i className="fa-solid fa-prescription-bottle-medical text-emerald-400 text-xs"></i>
                            <span>Rx Available</span>
                        </button>
                    )}
                    
                    {!remoteStream && (
                        <div className={`text-center z-10 p-6 md:p-10 max-w-lg mx-auto ${isDoctor ? 'mt-20 md:mt-16' : ''}`}>
                            <div className="w-20 h-20 md:w-28 md:h-28 bg-slate-900 rounded-[2.5rem] flex items-center justify-center text-slate-400 text-3xl md:text-4xl mx-auto mb-5 shadow-2xl border border-white/10 relative">
                                <i className={`fa-solid ${isDoctor ? 'fa-user' : 'fa-user-doctor'} ${isDoctor ? 'animate-pulse' : ''}`}></i>
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full border-2 border-slate-900 animate-ping"></span>
                            </div>
                            <h3 className="font-black text-lg md:text-2xl mb-2 text-white">
                                {isDoctor ? `Waiting for ${displayPatientName} to Join...` : `Waiting for ${displayDoctorName} to Enter...`}
                            </h3>
                            <p className="text-slate-400 font-bold text-[9px] md:text-xs uppercase tracking-[0.2em]">
                                {peerStatus === 'connected' ? 'Secure Consultation Active' : (isDoctor ? `Waiting for ${displayPatientName} to connect...` : `Connecting with ${displayDoctorName}...`)}
                            </p>

                            {isDoctor && (
                                <div className="mt-5">
                                    <button
                                        onClick={() => setShowRecordForm(true)}
                                        className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl text-xs font-bold transition-all shadow-lg shadow-indigo-600/30 flex items-center justify-center gap-2 mx-auto active:scale-95"
                                    >
                                        <i className="fa-solid fa-file-medical text-sm"></i>
                                        <span>Open Post-Consultation Form & Notes</span>
                                    </button>
                                </div>
                            )}

                            {(peerStatus === 'disconnected' || peerStatus === 'failed') && (
                                <div className="mt-5">
                                    <button
                                        onClick={reconnectCall}
                                        className="px-5 py-2.5 bg-teal-600 hover:bg-teal-500 text-white rounded-2xl text-xs font-bold transition-all shadow-lg shadow-teal-600/30 flex items-center justify-center gap-2 mx-auto active:scale-95 border border-teal-400/40"
                                    >
                                        <i className="fa-solid fa-arrows-rotate text-sm"></i>
                                        <span>Reconnect Call Stream</span>
                                    </button>
                                </div>
                            )}
                            
                            {errorMsg && (
                                <div className="mt-6 space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <div className="bg-rose-500/10 border border-rose-500/20 p-5 rounded-3xl text-left max-w-md mx-auto">
                                        <div className="flex items-center gap-3 text-rose-400 mb-2">
                                            <i className="fa-solid fa-triangle-exclamation"></i>
                                            <p className="font-black text-xs uppercase tracking-widest">Connection Notice</p>
                                        </div>
                                        <p className="text-rose-100 text-[10px] md:text-xs font-medium leading-relaxed">
                                            {errorMsg}. Please check your browser camera/microphone permissions or retry connecting.
                                        </p>
                                    </div>
                                    <button 
                                        onClick={() => { cleanupMedia(); startMedia(); }} 
                                        className="px-8 py-3 bg-indigo-600 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-500 transition-all shadow-lg shadow-indigo-500/25"
                                    >
                                        Retry Connection
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Movable & Dockable Local Video Stream (PiP) - Rock-solid positioning */}
                    <div 
                        ref={pipContainerRef}
                        style={pipOffset ? { left: `${pipOffset.x}px`, top: `${pipOffset.y}px`, bottom: 'auto', right: 'auto', touchAction: 'none' } : { touchAction: 'none' }}
                        className={`absolute z-20 group ${
                            pipOffset ? '' : (
                                pipCorner === 'bottom-right' ? 'bottom-24 right-4 md:bottom-12 md:right-10' :
                                pipCorner === 'bottom-left' ? 'bottom-24 left-4 md:bottom-12 md:left-10' :
                                pipCorner === 'top-left' ? 'top-20 left-4 md:top-8 md:left-10' :
                                'top-20 right-4 md:top-8 md:right-10'
                            )
                        } ${isPortraitMode ? 'w-24 sm:w-32 md:w-48 aspect-[9/16]' : 'w-28 sm:w-40 md:w-64 aspect-video'} bg-slate-900 rounded-2xl md:rounded-[2rem] shadow-2xl border-2 md:border-4 ${isDraggingPip ? 'border-indigo-400 shadow-indigo-500/30 ring-2 ring-indigo-400/50' : 'border-white/20 hover:border-white/40'} overflow-hidden transition-all ${isDraggingPip ? 'duration-0 cursor-grabbing' : 'duration-300'}`}
                    >
                        {/* Drag Header & Reposition Controls */}
                        <div 
                            onPointerDown={handlePipPointerDown}
                            className="absolute top-0 inset-x-0 h-8 bg-gradient-to-b from-black/90 via-black/50 to-transparent flex items-center justify-between px-2 z-30 cursor-grab active:cursor-grabbing select-none"
                            title="Hold and drag to move your camera anywhere on the screen"
                        >
                            <div className="flex items-center gap-1.5 text-white/90 text-[10px] font-bold pointer-events-none">
                                <i className="fa-solid fa-grip-vertical text-slate-400 text-[11px]"></i>
                                <span className="text-[9px] uppercase tracking-wider font-semibold text-slate-200">You</span>
                            </div>
                            <div className="flex items-center gap-1">
                                {hardwareZoomCapabilities && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const nextZoom = currentHardwareZoom > 1.2 ? 1 : Math.min(2, hardwareZoomCapabilities.max);
                                            handleSetCameraZoom(nextZoom);
                                        }}
                                        type="button"
                                        title={`Camera optical zoom: ${currentHardwareZoom}x`}
                                        className="px-1.5 h-5 rounded bg-white/20 hover:bg-white/40 text-white font-mono flex items-center justify-center text-[9px] font-bold transition-all active:scale-90"
                                    >
                                        {currentHardwareZoom}x
                                    </button>
                                )}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSwitchCameraFacing();
                                    }}
                                    type="button"
                                    title="Flip camera (Front/Back)"
                                    aria-label="Flip camera (Front/Back)"
                                    className="w-5 h-5 rounded bg-white/20 hover:bg-white/40 text-white flex items-center justify-center text-[9px] transition-all active:scale-90"
                                >
                                    <i className="fa-solid fa-camera-rotate"></i>
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setLocalVideoFit(f => f === 'cover' ? 'contain' : 'cover');
                                    }}
                                    type="button"
                                    title={localVideoFit === 'cover' ? "Zoom out self camera (Fit)" : "Fill self camera"}
                                    aria-label="Toggle self camera zoom"
                                    className="w-5 h-5 rounded bg-white/20 hover:bg-white/40 text-white flex items-center justify-center text-[9px] transition-all active:scale-90"
                                >
                                    <i className={`fa-solid ${localVideoFit === 'cover' ? 'fa-compress' : 'fa-expand'}`}></i>
                                </button>
                                <button
                                    onClick={cyclePipCorner}
                                    type="button"
                                    title="Snap camera to next corner"
                                    aria-label="Snap camera to next corner"
                                    className="w-5 h-5 rounded bg-white/20 hover:bg-white/40 text-white flex items-center justify-center text-[9px] transition-all active:scale-90"
                                >
                                    <i className="fa-solid fa-arrows-up-down-left-right"></i>
                                </button>
                            </div>
                        </div>

                        <video 
                            ref={videoRef} 
                            autoPlay 
                            muted 
                            playsInline 
                            className={`w-full h-full ${localVideoFit === 'contain' ? 'object-contain' : 'object-cover'} transform scale-x-[-1] ${isVideoOff ? 'opacity-0' : 'opacity-100'}`} 
                        />

                        {isVideoOff && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900 text-slate-400">
                                <i className="fa-solid fa-video-slash text-xl mb-1"></i>
                                <span className="text-[9px] font-bold uppercase tracking-wider">Camera Off</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Clinical Sidebar Panel */}
                {showPatientPanel && (
                    <div className="absolute right-0 top-0 bottom-0 w-full md:w-[30%] bg-white text-slate-900 z-[110] shadow-2xl animate-in slide-in-from-right duration-500 overflow-y-auto scrollbar-thin flex flex-col border-l border-slate-100">
                        <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50/50 sticky top-0 z-10">
                            <div className="flex space-x-4 overflow-x-auto">
                                {isDoctor ? (
                                    <>
                                        <button onClick={() => setActiveTab('profile')} className={`text-xs font-bold pb-1 transition-all whitespace-nowrap ${activeTab === 'profile' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}>Patient & Records</button>
                                        <button onClick={() => setActiveTab('notes')} className={`text-xs font-bold pb-1 transition-all whitespace-nowrap ${activeTab === 'notes' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}>Private Notes</button>
                                    </>
                                ) : (
                                    <>
                                        <button onClick={() => setActiveTab('profile')} className={`text-xs font-bold pb-1 transition-all whitespace-nowrap ${activeTab === 'profile' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}>Provider Info</button>
                                        <button onClick={() => setActiveTab('upload')} className={`text-xs font-bold pb-1 transition-all whitespace-nowrap ${activeTab === 'upload' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}>
                                            <i className="fa-solid fa-cloud-arrow-up mr-1 text-indigo-500"></i> Upload Records
                                        </button>
                                    </>
                                )}
                                <button onClick={() => setActiveTab('chat')} className={`text-xs font-bold pb-1 transition-all whitespace-nowrap ${activeTab === 'chat' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}>
                                    Chat {chatMessages.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-indigo-100 text-indigo-700 rounded-full font-black">{chatMessages.length}</span>}
                                </button>
                            </div>
                            <button onClick={() => setShowPatientPanel(false)} className="text-slate-400 hover:text-slate-600 ml-2"><i className="fa-solid fa-xmark text-lg"></i></button>
                        </div>

                        <div className="flex-grow bg-white overflow-y-auto">
                            {activeTab === 'profile' && (
                                <div className="p-6 md:p-8 space-y-8">
                                    {isDoctor && patientUser ? (
                                        <>
                                            {/* Verification Block */}
                                            <section className="space-y-6">
                                                <p className="text-[10px] font-black uppercase text-indigo-600 tracking-widest mb-4">Identity Verification</p>
                                                <div className="space-y-4">
                                                    {[
                                                        { id: 'idConfirmed', label: 'Patient Identity confirmed', icon: 'fa-id-card' },
                                                        { id: 'addressConfirmed', label: 'Home address confirmed', icon: 'fa-house-chimney-medical' },
                                                        { id: 'phoneConfirmed', label: 'Phone number confirmed', icon: 'fa-phone' },
                                                        { id: 'emailConfirmed', label: 'Email address confirmed', icon: 'fa-envelope' },
                                                        { id: 'consentConfirmed', label: 'Agreement to proceed confirmed', icon: 'fa-handshake-simple' }
                                                    ].map(item => (
                                                        <button 
                                                            key={item.id}
                                                            onClick={() => setVerification({...verification, [item.id]: !(verification as any)[item.id]})}
                                                            className={`w-full p-4 rounded-2xl flex items-center justify-between border-2 transition-all ${
                                                                (verification as any)[item.id] ? 'bg-indigo-50 border-indigo-200 text-indigo-900' : 'bg-slate-50 border-transparent text-slate-400'
                                                            }`}
                                                        >
                                                            <span className="text-xs font-black uppercase tracking-widest flex items-center gap-3">
                                                                <i className={`fa-solid ${item.icon}`}></i> {item.label}
                                                            </span>
                                                            <i className={`fa-solid ${(verification as any)[item.id] ? 'fa-circle-check text-indigo-600' : 'fa-circle text-slate-200'}`}></i>
                                                        </button>
                                                    ))}
                                                </div>
                                            </section>

                                            {/* Patient Profile Snapshot */}
                                            <section className="p-6 md:p-8 bg-slate-50 rounded-[3rem] border border-slate-100 space-y-6">
                                                <div className="flex items-center gap-5 border-b border-slate-200 pb-6">
                                                    <img src={patientUser.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=200'} className="w-14 h-14 rounded-2xl bg-white object-cover shadow-sm" alt="Patient" />
                                                    <div>
                                                        <h4 className="font-black text-slate-800">{patientUser.name}</h4>
                                                        <p className="text-xs font-bold text-slate-400">MRN: {patientUser.mrn || 'N/A'}</p>
                                                    </div>
                                                </div>

                                                <div className="space-y-4 text-xs font-bold text-slate-600">
                                                    <div className="flex justify-between">
                                                        <span className="text-slate-400">DOB / Age:</span>
                                                        <span>{patientUser.dob || 'Unspecified'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-slate-400">Biological Sex:</span>
                                                        <span>{patientUser.gender || 'Unspecified'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-slate-400">Blood Type:</span>
                                                        <span>{patientUser.bloodType || 'Unspecified'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-slate-400">Known Allergies:</span>
                                                        <span className="text-rose-500 font-bold">{patientUser.allergies || 'None documented'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-slate-400">Home Address:</span>
                                                        <span className="text-right max-w-[60%]">{patientUser.address || 'Standard Telehealth'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-slate-400">Phone Number:</span>
                                                        <span className="text-right font-mono">{patientUser.phone || 'Unspecified'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-slate-400">Email Address:</span>
                                                        <span className="text-right font-mono">{patientUser.email || 'Unspecified'}</span>
                                                    </div>
                                                </div>
                                            </section>

                                            {/* Digital e-Prescription Quick Tool for Doctor */}
                                            <div className="p-4 bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100 rounded-3xl space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2.5">
                                                        <div className="w-8 h-8 rounded-xl bg-indigo-600 text-white flex items-center justify-center text-xs shadow-sm">
                                                            <i className="fa-solid fa-prescription"></i>
                                                        </div>
                                                        <div>
                                                            <h5 className="font-bold text-xs text-indigo-950">Digital e-Prescription Tool</h5>
                                                            <p className="text-[10px] text-indigo-600 font-medium">Instant Rovita-branded Rx</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {allPatientPrescriptions.length > 0 && (
                                                            <button
                                                                onClick={() => {
                                                                    if (allPatientPrescriptions.length === 1) {
                                                                        setViewingPrescriptionRecord(allPatientPrescriptions[0]);
                                                                    } else {
                                                                        setShowPrescriptionHistoryModal(true);
                                                                    }
                                                                }}
                                                                className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all active:scale-95 flex items-center gap-1"
                                                                title="View Patient's Previous Prescriptions"
                                                            >
                                                                <i className="fa-solid fa-file-prescription text-[10px]"></i>
                                                                <span>Past Rx ({allPatientPrescriptions.length})</span>
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => setShowPrescriptionGenerator(true)}
                                                            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-sm transition-all active:scale-95 flex items-center gap-1.5"
                                                        >
                                                            <i className="fa-solid fa-plus text-[10px]"></i>
                                                            <span>New Rx</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Medical Vault & Patient Uploads */}
                                            <section className="space-y-4">
                                                <div className="flex items-center justify-between">
                                                    <p className="text-[10px] font-black uppercase text-indigo-600 tracking-widest">Medical History & Uploads ({patientHistory.length})</p>
                                                    <span className="text-[10px] text-slate-400 font-bold">Real-time sync</span>
                                                </div>
                                                {patientHistory.map(rec => (
                                                    <div key={rec.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-2.5">
                                                        <div className="flex justify-between items-start text-xs">
                                                            <div>
                                                                <span className="font-bold text-slate-800 block">{rec.diagnosis || 'Clinical Document'}</span>
                                                                {rec.authoredBy === 'patient' ? (
                                                                    <span className="inline-block mt-0.5 px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded text-[9px] font-bold">Patient Upload</span>
                                                                ) : rec.rxId ? (
                                                                    <span className="inline-block mt-0.5 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-[9px] font-bold">e-Prescription (Rx #{rec.rxId})</span>
                                                                ) : null}
                                                            </div>
                                                            <span className="text-slate-400 text-[11px] font-medium">{rec.date}</span>
                                                        </div>
                                                        {rec.notes && (
                                                            <p className="text-[11px] text-slate-600 font-medium leading-relaxed">{rec.notes}</p>
                                                        )}
                                                        {rec.prescription && (
                                                            <div className="p-2.5 bg-indigo-50/50 rounded-xl border border-indigo-100/60">
                                                                <p className="text-[11px] text-slate-700 font-bold mb-1.5">Rx: {rec.prescription}</p>
                                                                <button
                                                                    onClick={() => setViewingPrescriptionRecord(rec)}
                                                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white hover:bg-indigo-600 hover:text-white text-indigo-700 border border-indigo-200 rounded-lg text-[11px] font-bold transition-all shadow-2xs"
                                                                >
                                                                    <i className="fa-solid fa-file-prescription text-[10px]"></i>
                                                                    <span>View Official Rovita Rx</span>
                                                                </button>
                                                            </div>
                                                        )}
                                                        {rec.attachmentUrl && (
                                                            <div className="pt-2 border-t border-slate-200/60">
                                                                <a 
                                                                    href={getSafeUrl(rec.attachmentUrl)} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-xs font-bold transition-all"
                                                                >
                                                                    <i className="fa-solid fa-paperclip text-[11px]"></i>
                                                                    <span className="truncate max-w-[200px]">{rec.attachmentName || 'View Document'}</span>
                                                                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px]"></i>
                                                                </a>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                                {patientHistory.length === 0 && (
                                                    <p className="text-xs text-slate-400 italic">No previous consultations or uploads recorded.</p>
                                                )}
                                            </section>
                                        </>
                                    ) : (
                                        <div className="space-y-6">
                                            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl">
                                                <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center text-xl">
                                                    <i className="fa-solid fa-user-doctor"></i>
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-slate-800">{displayDoctorName}</h4>
                                                    <p className="text-xs text-slate-500">{doctorUser?.specialties?.[0] || 'General Practitioner'}</p>
                                                </div>
                                            </div>
                                            <div className="p-4 bg-indigo-50/50 rounded-2xl border border-indigo-100 text-xs text-slate-600 space-y-2">
                                                <p className="font-bold text-indigo-900">Secure Consultation</p>
                                                <p>This video session is fully encrypted end-to-end. Your provider will document your clinical summary and prescription directly during this call.</p>
                                            </div>

                                            {latestPrescriptionRecord && (
                                                <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200 text-xs text-slate-700 space-y-2.5">
                                                    <div className="flex items-center gap-2 text-emerald-800 font-black">
                                                        <i className="fa-solid fa-prescription-bottle-medical text-sm text-emerald-600"></i>
                                                        <span>Official Rovita Prescription Issued</span>
                                                    </div>
                                                    <p className="text-[11px] text-emerald-900 leading-relaxed font-medium">
                                                        Your doctor has issued an official digital prescription (Rx #{latestPrescriptionRecord.rxId || 'Official'}). It is saved to your Medical Records.
                                                    </p>
                                                    <button
                                                        onClick={() => setViewingPrescriptionRecord(latestPrescriptionRecord)}
                                                        className="w-full py-2.5 px-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all shadow-sm"
                                                    >
                                                        <i className="fa-solid fa-file-prescription"></i>
                                                        View & Print Prescription
                                                    </button>
                                                </div>
                                            )}

                                            <button 
                                                onClick={() => setActiveTab('upload')} 
                                                className="w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-xs font-bold flex items-center justify-center gap-2 transition-all shadow-md shadow-indigo-600/20"
                                            >
                                                <i className="fa-solid fa-cloud-arrow-up"></i>
                                                Upload Medical Records for Doctor
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Patient In-Call Medical Record Upload Tab */}
                            {activeTab === 'upload' && !isDoctor && (
                                <div className="p-6 md:p-8 space-y-6">
                                    <div>
                                        <h3 className="font-black text-slate-800 text-base mb-1">Upload Medical Records</h3>
                                        <p className="text-xs text-slate-500 leading-relaxed">
                                            Share lab results, previous prescriptions, imaging, or medical notes directly with your doctor during this call.
                                        </p>
                                    </div>

                                    {patientUploadFeedback && (
                                        <div className={`p-4 rounded-2xl text-xs font-bold leading-relaxed border ${
                                            patientUploadFeedback.type === 'success' 
                                                ? 'bg-emerald-50 text-emerald-800 border-emerald-200' 
                                                : 'bg-rose-50 text-rose-800 border-rose-200'
                                        }`}>
                                            <div className="flex items-center gap-2 mb-1">
                                                <i className={`fa-solid ${patientUploadFeedback.type === 'success' ? 'fa-circle-check text-emerald-600' : 'fa-circle-exclamation text-rose-600'}`}></i>
                                                <span>{patientUploadFeedback.type === 'success' ? 'Success' : 'Upload Error'}</span>
                                            </div>
                                            {patientUploadFeedback.text}
                                        </div>
                                    )}

                                    <form onSubmit={handlePatientUploadRecord} className="space-y-4">
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black uppercase text-slate-500 tracking-wider block">Document Title / Type</label>
                                            <input 
                                                type="text"
                                                className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none text-xs font-bold text-slate-800"
                                                placeholder="e.g. Blood Test Report, MRI Scan, Allergy List"
                                                value={patientUploadTitle}
                                                onChange={e => setPatientUploadTitle(e.target.value)}
                                            />
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black uppercase text-slate-500 tracking-wider block">Select File(s) (PDF, Images, Reports - Multiple Allowed)</label>
                                            <div className="p-4 border-2 border-dashed border-slate-300 hover:border-indigo-500 rounded-2xl bg-slate-50 transition-all text-center">
                                                <input 
                                                    type="file" 
                                                    id="patient-file-input"
                                                    multiple
                                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                    className="hidden" 
                                                    onChange={e => {
                                                        if (e.target.files && e.target.files.length > 0) {
                                                            const newFiles = Array.from(e.target.files);
                                                            setPatientUploadFiles(prev => [...prev, ...newFiles]);
                                                            if (!patientUploadTitle && newFiles[0]) {
                                                                setPatientUploadTitle(newFiles[0].name.replace(/\.[^/.]+$/, ""));
                                                            }
                                                        }
                                                    }}
                                                />
                                                <label htmlFor="patient-file-input" className="cursor-pointer block">
                                                    <i className="fa-solid fa-file-arrow-up text-2xl text-indigo-500 mb-2"></i>
                                                    <p className="text-xs font-bold text-slate-700">
                                                        {patientUploadFiles.length > 0 ? `${patientUploadFiles.length} file(s) selected • Click to choose more` : 'Click to choose file(s) or drag & drop'}
                                                    </p>
                                                    <p className="text-[10px] text-slate-400 mt-1">Supports PDF, PNG, JPG up to 25MB</p>
                                                </label>
                                            </div>

                                            {patientUploadFiles.length > 0 && (
                                                <div className="space-y-1.5 max-h-32 overflow-y-auto scrollbar-thin mt-2">
                                                    {patientUploadFiles.map((file, idx) => (
                                                        <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-xl border border-slate-200 text-xs">
                                                            <span className="font-bold text-slate-700 truncate" title={file.name}>
                                                                <i className="fa-solid fa-paperclip text-indigo-500 mr-1.5"></i>
                                                                {file.name} ({(file.size / 1024).toFixed(1)} KB)
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => setPatientUploadFiles(prev => prev.filter((_, i) => i !== idx))}
                                                                className="text-slate-400 hover:text-rose-600 px-1.5 py-0.5 ml-2"
                                                                title="Remove file"
                                                            >
                                                                <i className="fa-solid fa-xmark"></i>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black uppercase text-slate-500 tracking-wider block">Notes for Doctor (Optional)</label>
                                            <textarea 
                                                className="w-full p-3 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none text-xs font-medium text-slate-800 resize-none h-20"
                                                placeholder="Explain what this document shows or when the test was performed..."
                                                value={patientUploadNotes}
                                                onChange={e => setPatientUploadNotes(e.target.value)}
                                            />
                                        </div>

                                        <button 
                                            type="submit"
                                            disabled={patientUploadFiles.length === 0 || isPatientUploading}
                                            className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-2xl text-xs font-black uppercase tracking-wider transition-all shadow-lg shadow-indigo-600/30 flex items-center justify-center gap-2 active:scale-95"
                                        >
                                            {isPatientUploading ? (
                                                <>
                                                    <i className="fa-solid fa-spinner animate-spin"></i>
                                                    Uploading Document(s)...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa-solid fa-cloud-arrow-up"></i>
                                                    Upload & Share with Doctor
                                                </>
                                            )}
                                        </button>
                                    </form>
                                </div>
                            )}

                            {activeTab === 'notes' && isDoctor && (
                                <div className="p-6 md:p-8 space-y-4">
                                    <p className="text-[10px] font-black uppercase text-indigo-600 tracking-widest">Confidential Provider Scratchpad</p>
                                    <textarea
                                        className="w-full h-80 p-4 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none text-slate-700 font-medium text-sm resize-none"
                                        placeholder="Type quick clinical thoughts during the call (auto-saved)..."
                                        value={privateNotes}
                                        onChange={e => setPrivateNotes(e.target.value)}
                                    />
                                    <p className="text-[10px] text-slate-400">These notes are confidential to providers and not visible to the patient.</p>
                                </div>
                            )}

                            {activeTab === 'chat' && (
                                <div className="flex flex-col h-[400px]">
                                    <div className="flex-1 p-4 overflow-y-auto space-y-3">
                                        {chatMessages.map(msg => (
                                            <div key={msg.id} className={`flex flex-col ${msg.senderId === user?.id ? 'items-end' : 'items-start'}`}>
                                                <div className={`p-3 rounded-2xl max-w-[80%] text-xs font-bold ${msg.senderId === user?.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-800'}`}>
                                                    {msg.text}
                                                </div>
                                                <span className="text-[9px] text-slate-400 mt-1">{msg.senderName || (msg.senderId === user?.id ? 'You' : 'Participant')}</span>
                                            </div>
                                        ))}
                                        {chatMessages.length === 0 && (
                                            <p className="text-xs text-slate-400 text-center py-10">No chat messages yet.</p>
                                        )}
                                    </div>
                                    <div className="p-3 border-t border-slate-100 flex gap-2">
                                        <input 
                                            type="text" 
                                            className="flex-1 p-3 bg-slate-50 rounded-xl text-xs font-bold outline-none border focus:border-indigo-500" 
                                            placeholder="Type a message..." 
                                            value={newMessage} 
                                            onChange={e => setNewMessage(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && sendMessage()}
                                        />
                                        <button onClick={sendMessage} className="px-4 bg-indigo-600 text-white rounded-xl text-xs font-bold">Send</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Doctor's Post-Consultation Complete Clinical Form Modal */}
            {isDoctor && showRecordForm && (
                <div className="fixed inset-0 z-[250] bg-slate-950/95 backdrop-blur-md flex items-center justify-center p-4 md:p-8">
                    <div className="w-full max-w-6xl bg-white rounded-[2rem] md:rounded-[3rem] shadow-2xl overflow-hidden relative animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]">
                        {/* Modal Header */}
                        <div className="p-6 md:p-8 border-b border-slate-100 flex flex-wrap justify-between items-center bg-slate-50/70 gap-4">
                            <div>
                                <div className="flex items-center gap-3">
                                    <h3 className="text-xl md:text-2xl font-black tracking-tight text-slate-900">Post-Consultation Medical Summary & Plan</h3>
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-xs">
                                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                        Auto-saving locally
                                    </span>
                                </div>
                                <div className="flex flex-wrap items-center gap-3 mt-1.5">
                                    <span className="text-xs font-black uppercase tracking-widest text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-lg">
                                        Patient: {displayPatientName}
                                    </span>
                                    <span className="text-xs font-bold text-slate-400">
                                        Started: {new Date(startTime).toLocaleTimeString()}
                                    </span>
                                    {hasRestoredConsultationDraft && (
                                        <span className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-0.5 rounded-lg flex items-center gap-1">
                                            <i className="fa-solid fa-clock-rotate-left"></i> Restored unsaved draft
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button 
                                    onClick={() => {
                                        if (window.confirm("Are you sure you want to clear this saved draft?")) {
                                            if (typeof window !== 'undefined' && appointmentId) {
                                                try { localStorage.removeItem(`rovita_consultation_draft_${appointmentId}`); } catch (e) {}
                                            }
                                            setRecordData({
                                                presentingSymptoms: '',
                                                history: '',
                                                pastHistory: '',
                                                socialHistory: '',
                                                vitals: '',
                                                physicalExam: '',
                                                labReports: '',
                                                imaging: '',
                                                diagnosis: '', 
                                                treatmentPlan: '', 
                                                prescription: '', 
                                                followUp: '',
                                                referrals: '',
                                                notes: ''
                                            });
                                            setHasRestoredConsultationDraft(false);
                                        }
                                    }}
                                    title="Reset / Clear Draft"
                                    className="px-3 py-2 bg-white text-slate-500 hover:text-rose-600 hover:bg-rose-50 border border-slate-200 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-xs"
                                >
                                    <i className="fa-solid fa-trash-can text-xs"></i>
                                    <span className="hidden sm:inline">Clear Draft</span>
                                </button>
                                <button onClick={() => setShowRecordForm(false)} className="w-10 h-10 flex items-center justify-center bg-white rounded-2xl text-slate-400 hover:text-slate-700 hover:shadow-md transition-all border border-slate-200">
                                    <i className="fa-solid fa-xmark text-lg"></i>
                                </button>
                            </div>
                        </div>

                        {/* Modal Body: All 16 Clinical Fields */}
                        <div className="p-6 md:p-10 bg-white flex-1 overflow-y-auto scrollbar-thin">
                            <div className="grid lg:grid-cols-2 gap-8 md:gap-10">
                                
                                {/* Section 1: Subjective & History */}
                                <div className="space-y-6">
                                    <div className="pb-2 border-b border-indigo-100 flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-sm font-black">1</div>
                                        <h4 className="text-xs font-black uppercase text-indigo-700 tracking-widest">
                                            Subjective Assessment & Patient History
                                        </h4>
                                    </div>
                                    
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black uppercase text-slate-500 ml-2 tracking-widest">Presenting Symptoms / Chief Complaint (CC)</label>
                                        <input 
                                            className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none font-bold text-slate-800 placeholder-slate-400 text-sm transition-all" 
                                            value={recordData.presentingSymptoms} 
                                            onChange={e => setRecordData({...recordData, presentingSymptoms: e.target.value})} 
                                            placeholder="e.g. Acute sore throat, fever 38.5C for 2 days..." 
                                        />
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black uppercase text-slate-500 ml-2 tracking-widest">History of Present Illness (HPI)</label>
                                        <textarea 
                                            className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none h-24 font-medium resize-none text-slate-800 placeholder-slate-400 text-sm transition-all" 
                                            value={recordData.history} 
                                            onChange={e => setRecordData({...recordData, history: e.target.value})} 
                                            placeholder="Onset, duration, severity, modifying factors, progression..." 
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black uppercase text-slate-500 ml-2 tracking-widest">Past Medical History (PMHx)</label>
                                            <textarea 
                                                className="w-full p-3 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none h-20 font-medium resize-none text-slate-800 placeholder-slate-400 text-xs transition-all" 
                                                value={recordData.pastHistory} 
                                                onChange={e => setRecordData({...recordData, pastHistory: e.target.value})} 
                                                placeholder="Chronic conditions, prior surgeries..." 
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black uppercase text-slate-500 ml-2 tracking-widest">Social & Lifestyle History</label>
                                            <textarea 
                                                className="w-full p-3 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none h-20 font-medium resize-none text-slate-800 placeholder-slate-400 text-xs transition-all" 
                                                value={recordData.socialHistory} 
                                                onChange={e => setRecordData({...recordData, socialHistory: e.target.value})} 
                                                placeholder="Smoking, alcohol, occupation, exercise..." 
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Section 2: Objective & Findings */}
                                <div className="space-y-6">
                                    <div className="pb-2 border-b border-indigo-100 flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-sm font-black">2</div>
                                        <h4 className="text-xs font-black uppercase text-indigo-700 tracking-widest">
                                            Objective Observations & Investigations
                                        </h4>
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black uppercase text-slate-500 ml-2 tracking-widest">Vital Signs (BP, HR, Temp, SpO2, RR)</label>
                                        <input 
                                            className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none font-bold text-slate-800 placeholder-slate-400 text-sm transition-all" 
                                            value={recordData.vitals} 
                                            onChange={e => setRecordData({...recordData, vitals: e.target.value})} 
                                            placeholder="e.g. BP 120/80, HR 72, Temp 37.0°C, SpO2 99%" 
                                        />
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black uppercase text-slate-500 ml-2 tracking-widest">Physical & Visual Examination Findings</label>
                                        <textarea 
                                            className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none h-24 font-medium resize-none text-slate-800 placeholder-slate-400 text-sm transition-all" 
                                            value={recordData.physicalExam} 
                                            onChange={e => setRecordData({...recordData, physicalExam: e.target.value})} 
                                            placeholder="Observed appearance, skin, respiratory effort, throat, etc..." 
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black uppercase text-slate-500 ml-2 tracking-widest">Lab Reports / Diagnostic Orders</label>
                                            <input 
                                                className="w-full p-3 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none font-medium text-slate-800 placeholder-slate-400 text-xs transition-all" 
                                                value={recordData.labReports} 
                                                onChange={e => setRecordData({...recordData, labReports: e.target.value})} 
                                                placeholder="CBC, CRP, Lipid panel..." 
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black uppercase text-slate-500 ml-2 tracking-widest">Radiology & Imaging Studies</label>
                                            <input 
                                                className="w-full p-3 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none font-medium text-slate-800 placeholder-slate-400 text-xs transition-all" 
                                                value={recordData.imaging} 
                                                onChange={e => setRecordData({...recordData, imaging: e.target.value})} 
                                                placeholder="Chest X-Ray, Ultrasound, CT..." 
                                            />
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Section 3: Assessment, Plan & Prescription */}
                                <div className="lg:col-span-2 space-y-6 pt-6 border-t border-slate-100">
                                    <div className="pb-2 border-b border-indigo-100 flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-sm font-black">3</div>
                                        <h4 className="text-xs font-black uppercase text-indigo-700 tracking-widest">
                                            Assessment, Prescription & Management Plan
                                        </h4>
                                    </div>
                                    
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black uppercase text-slate-500 ml-2 tracking-widest">
                                            Primary Clinical Diagnosis / Impression <span className="text-rose-500">*</span>
                                        </label>
                                        <input 
                                            className="w-full p-4 bg-emerald-50/60 rounded-2xl border-2 border-emerald-200 focus:border-emerald-500 outline-none font-black text-lg text-emerald-950 placeholder-emerald-400 transition-all" 
                                            value={recordData.diagnosis} 
                                            onChange={e => setRecordData({...recordData, diagnosis: e.target.value})} 
                                            placeholder="e.g. Acute Viral Pharyngitis" 
                                        />
                                    </div>

                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black uppercase text-slate-500 ml-2 tracking-widest">Treatment Plan & Directives</label>
                                            <textarea 
                                                className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none h-28 font-medium resize-none text-slate-800 placeholder-slate-400 text-sm transition-all" 
                                                value={recordData.treatmentPlan} 
                                                onChange={e => setRecordData({...recordData, treatmentPlan: e.target.value})} 
                                                placeholder="Rest, hydration, symptomatic relief protocol..." 
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <div className="flex items-center justify-between ml-2">
                                                <label className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Prescription & Dosage (Rx)</label>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPrescriptionGenerator(true)}
                                                    className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 underline transition-colors"
                                                >
                                                    <i className="fa-solid fa-prescription"></i>
                                                    <span>Open Rovita e-Rx Builder</span>
                                                </button>
                                            </div>
                                            <textarea 
                                                className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none h-28 font-medium resize-none text-slate-800 placeholder-slate-400 text-sm transition-all" 
                                                value={recordData.prescription} 
                                                onChange={e => setRecordData({...recordData, prescription: e.target.value})} 
                                                placeholder="e.g. Amoxicillin 500mg PO TID x 7 days, Paracetamol 1g PRN..." 
                                            />
                                        </div>
                                    </div>

                                    <div className="grid md:grid-cols-3 gap-6">
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black uppercase text-slate-500 ml-2 tracking-widest">Follow-Up Instructions</label>
                                            <input 
                                                className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none font-medium text-slate-800 placeholder-slate-400 text-xs transition-all" 
                                                placeholder="e.g. Review in 5 days if unresolved" 
                                                value={recordData.followUp} 
                                                onChange={e => setRecordData({...recordData, followUp: e.target.value})} 
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black uppercase text-slate-500 ml-2 tracking-widest">Specialist Referrals Issued</label>
                                            <input 
                                                className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none font-medium text-slate-800 placeholder-slate-400 text-xs transition-all" 
                                                placeholder="e.g. ENT referral, Cardiology consult" 
                                                value={recordData.referrals} 
                                                onChange={e => setRecordData({...recordData, referrals: e.target.value})} 
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black uppercase text-slate-500 ml-2 tracking-widest">Patient Advice / Summary Notes</label>
                                            <input 
                                                className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none font-medium text-slate-800 placeholder-slate-400 text-xs transition-all" 
                                                placeholder="General dietary and rest advice..." 
                                                value={recordData.notes} 
                                                onChange={e => setRecordData({...recordData, notes: e.target.value})} 
                                            />
                                        </div>
                                    </div>

                                    {/* Clinical Document / PDF Attachment Upload */}
                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
                                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                            <div>
                                                <p className="text-xs font-black text-slate-800">Attach Clinical Documents or Reports (Multiple Supported)</p>
                                                <p className="text-[11px] text-slate-500 font-medium">
                                                    {selectedAttachments.length > 0 ? `${selectedAttachments.length} file(s) attached` : 'Optional: Upload lab requisitions, referral letters, or signed PDFs'}
                                                </p>
                                            </div>
                                            <label className="cursor-pointer px-4 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-700 hover:bg-slate-50 transition-all shrink-0">
                                                <i className="fa-solid fa-paperclip mr-1.5 text-indigo-600"></i>
                                                {selectedAttachments.length > 0 ? 'Add More Files' : 'Choose Files'}
                                                <input 
                                                    type="file" 
                                                    multiple
                                                    className="hidden" 
                                                    accept=".pdf,image/*,.doc,.docx"
                                                    onChange={e => {
                                                        if (e.target.files && e.target.files.length > 0) {
                                                            const newFiles = Array.from(e.target.files);
                                                            setSelectedAttachments(prev => [...prev, ...newFiles]);
                                                        }
                                                    }}
                                                />
                                            </label>
                                        </div>

                                        {selectedAttachments.length > 0 && (
                                            <div className="space-y-1.5 max-h-32 overflow-y-auto scrollbar-thin pt-2 border-t border-slate-200">
                                                {selectedAttachments.map((file, idx) => (
                                                    <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-xl border border-slate-200 text-xs">
                                                        <span className="font-bold text-slate-700 truncate" title={file.name}>
                                                            <i className="fa-solid fa-paperclip text-indigo-500 mr-1.5"></i>
                                                            {file.name} ({(file.size / 1024).toFixed(1)} KB)
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => setSelectedAttachments(prev => prev.filter((_, i) => i !== idx))}
                                                            className="text-slate-400 hover:text-rose-600 px-1.5 py-0.5 ml-2"
                                                            title="Remove attachment"
                                                        >
                                                            <i className="fa-solid fa-xmark"></i>
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Modal Footer */}
                        <div className="p-6 md:p-8 bg-slate-50 border-t border-slate-100 flex flex-col md:flex-row gap-6 items-center">
                            <div className="flex-grow text-center md:text-left">
                                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">
                                    <i className="fa-solid fa-lock mr-1 text-emerald-600"></i> Secure Health Record
                                </p>
                                <p className="text-sm md:text-base font-bold text-slate-700 mt-0.5">
                                    This summary will be permanently saved to the patient's medical vault.
                                </p>
                            </div>
                            <div className="w-full md:w-auto flex flex-wrap gap-4 items-center">
                                <Button onClick={() => setShowRecordForm(false)} variant="ghost" className="flex-1 md:flex-none">Back to Call</Button>
                                <button 
                                    onClick={() => handleEndSession()}
                                    type="button"
                                    className="flex-1 md:flex-none py-3 px-4 text-xs font-bold text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-2xl transition-all"
                                >
                                    Leave Without Saving
                                </button>
                                <Button onClick={submitRecord} disabled={isUploading} className="flex-1 md:flex-none py-4 px-8 text-base shadow-indigo-500/30">
                                    {isUploading ? 'Saving Record...' : 'Finalize & End Consultation'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Bottom Call Controls Bar */}
            <div className="p-6 md:p-8 bg-slate-900 border-t border-white/5 flex justify-center items-center gap-4 md:gap-8 pb-10 md:pb-8" role="region" aria-label="Call Controls">
                <button 
                  onClick={toggleAudio} 
                  title={isMuted ? "Unmute Microphone" : "Mute Microphone"}
                  aria-label={isMuted ? "Unmute Microphone" : "Mute Microphone"}
                  aria-pressed={isMuted}
                  className={`w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center text-lg md:text-xl transition-all border outline-none ${isMuted ? 'bg-rose-500 text-white border-rose-500 shadow-lg' : 'bg-transparent text-slate-300 border-slate-600 hover:bg-slate-800 hover:text-white'}`}>
                  <i className={`fa-solid ${isMuted ? 'fa-microphone-slash' : 'fa-microphone'}`} aria-hidden="true"></i>
                </button>
                <button 
                  onClick={toggleVideo} 
                  title={isVideoOff ? "Turn On Camera" : "Turn Off Camera"}
                  aria-label={isVideoOff ? "Turn On Camera" : "Turn Off Camera"}
                  aria-pressed={isVideoOff}
                  className={`w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center text-lg md:text-xl transition-all border outline-none ${isVideoOff ? 'bg-rose-500 text-white border-rose-500 shadow-lg' : 'bg-transparent text-slate-300 border-slate-600 hover:bg-slate-800 hover:text-white'}`}>
                  <i className={`fa-solid ${isVideoOff ? 'fa-video-slash' : 'fa-video'}`} aria-hidden="true"></i>
                </button>
                <button 
                  onClick={toggleCameraOrientation} 
                  title={isPortraitMode ? "Switch to Landscape Mode" : "Switch to Mobile Portrait Mode"}
                  aria-label={isPortraitMode ? "Switch to Landscape Mode" : "Switch to Mobile Portrait Mode"}
                  aria-pressed={isPortraitMode}
                  className={`w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center text-lg md:text-xl transition-all border outline-none ${isPortraitMode ? 'bg-indigo-600/30 text-indigo-300 border-indigo-500/50' : 'bg-transparent text-slate-300 border-slate-600 hover:bg-slate-800 hover:text-white'}`}>
                  <i className={`fa-solid ${isPortraitMode ? 'fa-mobile-screen-button' : 'fa-laptop'}`} aria-hidden="true"></i>
                </button>
                {peerStatus !== 'connected' && (
                    <button 
                      onClick={reconnectCall} 
                      title="Reconnect Consultation Link"
                      aria-label="Reconnect Consultation Link"
                      className="w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center text-lg md:text-xl transition-all border outline-none bg-teal-600/30 text-teal-300 border-teal-500/50 hover:bg-teal-600 hover:text-white animate-pulse">
                      <i className="fa-solid fa-arrows-rotate" aria-hidden="true"></i>
                    </button>
                )}
                {/* For Doctor: View Previous Prescriptions button (when previous prescriptions exist) */}
                {isDoctor && allPatientPrescriptions.length > 0 && (
                    <button 
                      onClick={() => {
                        if (allPatientPrescriptions.length === 1) {
                          setViewingPrescriptionRecord(allPatientPrescriptions[0]);
                        } else {
                          setShowPrescriptionHistoryModal(true);
                        }
                      }} 
                      title={`View Patient's Previous Prescriptions (${allPatientPrescriptions.length})`}
                      aria-label="View Patient's Previous Prescriptions"
                      className="relative w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center text-lg md:text-xl transition-all border outline-none bg-emerald-600/30 text-emerald-300 border-emerald-500/50 hover:bg-emerald-600 hover:text-white ring-2 ring-emerald-500/40"
                    >
                      <i className="fa-solid fa-file-prescription" aria-hidden="true"></i>
                      <span className="absolute -top-1 -right-1 bg-emerald-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full border border-slate-900 shadow-sm leading-none">
                        {allPatientPrescriptions.length}
                      </span>
                    </button>
                )}
                {/* For Doctor: Distinct button to write/generate new prescription */}
                {isDoctor && (
                    <button 
                      onClick={() => setShowPrescriptionGenerator(true)} 
                      title="Generate New Prescription (e-Rx)"
                      aria-label="Generate New Prescription (e-Rx)"
                      className="w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center text-lg md:text-xl transition-all border outline-none bg-indigo-600 hover:bg-indigo-500 text-white border-indigo-400/50 shadow-lg shadow-indigo-600/30 ring-2 ring-indigo-400/30"
                    >
                      <i className="fa-solid fa-file-circle-plus text-base md:text-xl" aria-hidden="true"></i>
                    </button>
                )}
                {/* For Patient: View Official Prescriptions */}
                {!isDoctor && allPatientPrescriptions.length > 0 && (
                    <button 
                      onClick={() => {
                        if (allPatientPrescriptions.length === 1) {
                          setViewingPrescriptionRecord(allPatientPrescriptions[0]);
                        } else {
                          setShowPrescriptionHistoryModal(true);
                        }
                      }} 
                      title="View Official Rovita Digital Prescription"
                      aria-label="View Official Rovita Digital Prescription"
                      className="relative w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center text-lg md:text-xl transition-all border outline-none bg-emerald-600/30 text-emerald-300 border-emerald-500/50 hover:bg-emerald-600 hover:text-white ring-2 ring-emerald-500/40"
                    >
                      <i className="fa-solid fa-file-prescription" aria-hidden="true"></i>
                      {allPatientPrescriptions.length > 1 && (
                        <span className="absolute -top-1 -right-1 bg-emerald-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full border border-slate-900 shadow-sm leading-none">
                          {allPatientPrescriptions.length}
                        </span>
                      )}
                    </button>
                )}
                {!isDoctor && (
                    <button 
                      onClick={() => {
                        setActiveTab('upload');
                        setShowPatientPanel(true);
                      }} 
                      title="Upload Medical Records & Documents for Doctor"
                      aria-label="Upload Medical Records & Documents for Doctor"
                      className="w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center text-lg md:text-xl transition-all border outline-none bg-indigo-600/30 text-indigo-300 border-indigo-500/50 hover:bg-indigo-600 hover:text-white">
                      <i className="fa-solid fa-cloud-arrow-up" aria-hidden="true"></i>
                    </button>
                )}
                <button 
                  onClick={() => isDoctor ? setShowRecordForm(true) : handleEndSession()} 
                  title="End Consultation Session"
                  aria-label="End Consultation Session"
                  className="w-16 h-16 md:w-20 md:h-20 bg-rose-500 text-white rounded-[2rem] flex items-center justify-center text-2xl md:text-3xl shadow-lg shadow-rose-500/30 hover:bg-rose-600 transition-all ml-2">
                  <i className="fa-solid fa-phone-slash" aria-hidden="true"></i>
                </button>
            </div>

            {/* Digital Prescription Generator Modal for Doctors */}
            {showPrescriptionGenerator && (
                <DigitalPrescriptionGeneratorModal
                    patientId={appointment?.patientId || ''}
                    patientName={displayPatientName}
                    patientMrn={patientUser?.mrn}
                    patientAge={patientUser?.dob ? `${patientUser.dob}` : 'Adult'}
                    patientGender={patientUser?.gender}
                    patientAllergies={patientUser?.allergies}
                    appointmentId={appointmentId}
                    initialDiagnosis={recordData.diagnosis}
                    initialNotes={recordData.notes}
                    onClose={() => setShowPrescriptionGenerator(false)}
                    onPrescriptionIssued={(rxId, items) => {
                        setIssuedPrescriptionRxId(rxId);
                        if (items && items.length > 0) {
                            const formatted = items.map(item => `${item.name} ${item.dosage} (${item.frequency}, ${item.duration})`).join('; ');
                            setRecordData(prev => ({
                                ...prev,
                                prescription: prev.prescription ? `${prev.prescription}\n${formatted}` : formatted
                            }));
                        }
                        if (user) {
                            const db = getFirestore(getApp());
                            addDoc(collection(db, 'calls', appointmentId, 'messages'), {
                                senderId: user.id,
                                senderName: user.name,
                                text: `💊 Official Digital Prescription (${rxId}) has been generated and added to your Health Vault. You can view, download, or print it right now.`,
                                timestamp: new Date().toISOString()
                            }).catch(() => {});
                        }
                    }}
                />
            )}

            {/* Read-Only Digital Prescription Viewer/Print Modal with Rovita Branding */}
            {viewingPrescriptionRecord && (
                <DigitalPrescriptionModal
                    record={viewingPrescriptionRecord}
                    hideDiagnosis={!isDoctor}
                    onWriteNew={isDoctor ? () => setShowPrescriptionGenerator(true) : undefined}
                    onClose={() => setViewingPrescriptionRecord(null)}
                />
            )}

            {/* Previous Prescriptions History Modal */}
            {showPrescriptionHistoryModal && (
                <div className="fixed inset-0 z-[190] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-4">
                    <div className="bg-white text-slate-900 rounded-3xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl border border-slate-100 overflow-hidden animate-in zoom-in-95 duration-200">
                        {/* Header */}
                        <div className="p-4 sm:p-6 bg-slate-900 text-white flex items-center justify-between shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-2xl bg-emerald-600/30 border border-emerald-500/40 flex items-center justify-center text-emerald-400 shrink-0">
                                    <i className="fa-solid fa-file-prescription text-lg"></i>
                                </div>
                                <div>
                                    <h3 className="text-base sm:text-lg font-black text-white">Previous Prescriptions</h3>
                                    <p className="text-xs text-slate-400 font-medium">
                                        Patient: <span className="text-emerald-400 font-bold">{patientUser?.name || 'Current Patient'}</span>
                                        {patientUser?.mrn && <span className="ml-2 font-mono text-[11px] bg-slate-800 px-2 py-0.5 rounded text-slate-300">{patientUser.mrn}</span>}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {isDoctor && (
                                    <button
                                        onClick={() => {
                                            setShowPrescriptionHistoryModal(false);
                                            setShowPrescriptionGenerator(true);
                                        }}
                                        className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold transition-all shadow-md active:scale-95 flex items-center gap-1.5"
                                        title="Generate New Prescription"
                                    >
                                        <i className="fa-solid fa-plus text-xs"></i>
                                        <span>New Prescription</span>
                                    </button>
                                )}
                                <button
                                    onClick={() => setShowPrescriptionHistoryModal(false)}
                                    className="w-8 h-8 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-all"
                                    aria-label="Close prescription history"
                                >
                                    <i className="fa-solid fa-xmark text-sm"></i>
                                </button>
                            </div>
                        </div>

                        {/* List of Prescriptions */}
                        <div className="p-4 sm:p-6 overflow-y-auto space-y-3 flex-1 scrollbar-thin">
                            {allPatientPrescriptions.map((rx) => {
                                const itemCount = rx.prescriptionItems?.length || 0;
                                return (
                                    <div 
                                        key={rx.id}
                                        className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 hover:border-indigo-300 hover:bg-indigo-50/20 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                                    >
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-mono font-bold text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-md">
                                                    {rx.rxId || `Rx #${rx.id.slice(0, 8)}`}
                                                </span>
                                                <span className="text-xs font-bold text-slate-500">{rx.date}</span>
                                                {rx.doctorName && (
                                                    <span className="text-xs font-medium text-slate-600">
                                                        by <strong className="text-slate-800">{rx.doctorName}</strong>
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs font-bold text-slate-800">
                                                <span className="text-slate-500 font-medium">Diagnosis:</span> {rx.diagnosis || 'Clinical Consultation'}
                                            </p>
                                            {itemCount > 0 ? (
                                                <p className="text-xs text-slate-600">
                                                    <span className="font-semibold text-slate-700">{itemCount} Medication{itemCount > 1 ? 's' : ''}:</span> {rx.prescriptionItems?.map(it => it.name).join(', ')}
                                                </p>
                                            ) : rx.prescription ? (
                                                <p className="text-xs text-slate-600 truncate max-w-md">
                                                    {rx.prescription}
                                                </p>
                                            ) : null}
                                        </div>

                                        <button
                                            onClick={() => {
                                                setShowPrescriptionHistoryModal(false);
                                                setViewingPrescriptionRecord(rx);
                                            }}
                                            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold transition-all shadow-sm active:scale-95 flex items-center justify-center gap-1.5 shrink-0 self-start sm:self-center"
                                        >
                                            <i className="fa-solid fa-eye text-xs"></i>
                                            <span>View & Print Rx</span>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between shrink-0">
                            <span className="text-xs text-slate-500 font-medium">
                                Showing {allPatientPrescriptions.length} official prescription{allPatientPrescriptions.length > 1 ? 's' : ''}
                            </span>
                            {isDoctor ? (
                                <button
                                    onClick={() => {
                                        setShowPrescriptionHistoryModal(false);
                                        setShowPrescriptionGenerator(true);
                                    }}
                                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold transition-all shadow-md active:scale-95 flex items-center gap-1.5"
                                >
                                    <i className="fa-solid fa-plus text-xs"></i>
                                    <span>Create Another Prescription</span>
                                </button>
                            ) : (
                                <button
                                    onClick={() => setShowPrescriptionHistoryModal(false)}
                                    className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl text-xs font-bold transition-all"
                                >
                                    Close
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* 2-Minute Patient No-Response Disconnect Modal for Doctors */}
            {showPatientNoResponseModal && (
                <div className="fixed inset-0 z-[200] bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-slate-700 text-white rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-200 text-center font-['Plus_Jakarta_Sans']">
                        <div className="w-16 h-16 rounded-2xl bg-teal-500/20 text-teal-400 flex items-center justify-center text-2xl mx-auto mb-4 border border-teal-500/30">
                            <i className="fa-solid fa-clock"></i>
                        </div>
                        <h3 className="text-xl font-black mb-2 text-white">Patient is Unavailable</h3>
                        <p className="text-slate-300 text-xs md:text-sm leading-relaxed mb-6">
                            Your patient hasn't connected to the room yet. We emailed them a direct link and sent an in-app notice. You can choose to wait a little longer or return to your dashboard.
                        </p>
                        <div className="flex flex-col gap-2.5">
                            <button
                                onClick={() => {
                                    setShowPatientNoResponseModal(false);
                                    setDoctorWaitingSeconds(0);
                                }}
                                className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs uppercase tracking-wider rounded-2xl transition-all shadow-lg shadow-emerald-600/30 active:scale-95 cursor-pointer flex items-center justify-center gap-2"
                            >
                                <i className="fa-solid fa-hourglass-half"></i>
                                <span>Wait 2 More Minutes</span>
                            </button>
                            <button
                                onClick={() => {
                                    setShowPatientNoResponseModal(false);
                                    handleEndSession(false);
                                }}
                                className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs uppercase tracking-wider rounded-2xl transition-all active:scale-95 cursor-pointer"
                            >
                                Return to Dashboard
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Consultation Concluded Modal when other participant ends call */}
            {callEndedByRemote && (
                <div className="fixed inset-0 z-[250] bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-slate-700 text-white rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-200 text-center font-['Plus_Jakarta_Sans']">
                        <div className="w-16 h-16 rounded-2xl bg-indigo-500/20 text-indigo-400 flex items-center justify-center text-2xl mx-auto mb-4 border border-indigo-500/30">
                            <i className="fa-solid fa-phone-slash"></i>
                        </div>
                        <h3 className="text-xl font-black mb-2 text-white">Consultation Concluded</h3>
                        <p className="text-slate-300 text-xs md:text-sm leading-relaxed mb-6 font-medium">
                            {callEndedByRemote} has ended the consultation session.
                        </p>
                        <div className="flex gap-3">
                            {isDoctor && (
                                <button
                                    onClick={() => {
                                        setCallEndedByRemote(null);
                                        setShowRecordForm(true);
                                    }}
                                    className="flex-1 py-3.5 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs uppercase tracking-wider rounded-2xl transition-all cursor-pointer"
                                >
                                    Clinical Record
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    setCallEndedByRemote(null);
                                    onEnd();
                                }}
                                className="flex-1 py-3.5 bg-indigo-600 hover:bg-indigo-500 text-white font-black text-xs uppercase tracking-wider rounded-2xl transition-all shadow-lg shadow-indigo-600/30 active:scale-95 cursor-pointer"
                            >
                                Return to Dashboard
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
